import { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import CompleteProjectButton from "@/components/dashboard/CompleteProjectButton";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import { ChevronLeft, ChevronDown, ChevronUp, Plus, Minus, Trash2, Save, ExternalLink, Undo2, Redo2, Mail, RotateCcw, Pencil, RefreshCw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { CustomerPicker } from "@/components/dashboard/CustomerPicker";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const UNIT_OPTIONS = ["Linear ft", "Item qty", "Inches", "Per roll", "Labour"];

// Searchable customer picker for the edit dialog — sorted numbers-first, then A–Z.
const money = (n) =>
  n === null || n === undefined || n === ""
    ? ""
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n) || 0);
// Percent helper: input is a RATIO (0.011 = 1.10%). Shows 2 decimals so
// small percentages don't round away (1.10% not 1%), trimming only ".00".
const pct = (n) => {
  if (n === null || n === undefined || n === "") return "";
  const s = (Number(n) * 100).toFixed(2);
  return `${s.replace(/\.00$/, "")}%`;
};
const num = (n) => (n === null || n === undefined || n === "" ? "" : Number(n).toLocaleString("en-US"));
const fmtDateTime = (iso) => {
  if (!iso || iso === "unknown") return "—";
  try {
    return new Date(iso).toLocaleString("en-CA", { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

// Match the pill logic on the Projects list — legacy statuses like
// "In Progress" / "Completed" / "On Hold" all collapse to Active/Inactive so
// the detail header agrees with what the list shows.
const displayStatus = (s) => {
  if (!s) return "Active";
  if (s === "Inactive" || s === "Completed" || s === "On Hold") return "Inactive";
  return "Active";
};


/** Red box at the top of a project page: every rip-out report filed against
 * this job, with its photos. Loads quietly — staff without admin rights (and
 * jobs with no rip-outs) simply don't see it. */
const ProjectRipouts = ({ projectId }) => {
  const [items, setItems] = useState(null);
  const [thumbs, setThumbs] = useState({}); // `${rid}:${idx}` -> objectURL
  const [viewer, setViewer] = useState(null); // objectURL
  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/rip-outs", { params: { project_id: projectId } });
        if (!alive) return;
        setItems(data || []);
        // preload thumbnails
        (data || []).forEach((r) => {
          for (let i = 0; i < (r.photo_count || 0); i += 1) {
            const key = `${r.ripout_id}:${i}`;
            api.get(`/rip-outs/${r.ripout_id}/photos/${i}`, { responseType: "blob" })
              .then((res) => { if (alive) setThumbs((t) => (t[key] ? t : { ...t, [key]: URL.createObjectURL(res.data) })); })
              .catch(() => {});
          }
        });
      } catch { if (alive) setItems([]); }
    })();
    return () => { alive = false; };
  }, [projectId]);
  if (!items || items.length === 0) return null;
  return (
    <div className="rounded-lg border-2 border-red-500 bg-red-50 p-4" data-testid="project-ripouts-box">
      <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-red-700">
        Rip-out reports ({items.length})
      </p>
      <div className="mt-2 space-y-3">
        {items.map((r) => (
          <div key={r.ripout_id} className="rounded-md border border-red-200 bg-white p-3" data-testid={`project-ripout-${r.ripout_id}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-sm font-semibold text-gray-900">
                {(r.occurred_at || "").replace("T", " ")} — {r.address || r.project_name}
              </p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">by {r.employee_name}</p>
            </div>
            {r.notes && <p className="mt-1 whitespace-pre-wrap text-xs text-gray-700">{r.notes}</p>}
            {r.photo_count > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {Array.from({ length: r.photo_count }).map((_, i) => {
                  const key = `${r.ripout_id}:${i}`;
                  const url = thumbs[key];
                  return url ? (
                    <button key={key} type="button" onClick={() => setViewer(url)}
                      className="h-16 w-16 overflow-hidden rounded-md border border-red-200"
                      data-testid={`project-ripout-photo-${r.ripout_id}-${i}`}>
                      <img src={url} alt={`Rip-out ${i + 1}`} className="h-full w-full object-cover" />
                    </button>
                  ) : (
                    <span key={key} className="h-16 w-16 animate-pulse rounded-md bg-red-100" />
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
      {viewer && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/80 p-4" onClick={() => setViewer(null)} data-testid="project-ripout-viewer">
          <img src={viewer} alt="Rip-out evidence" className="max-h-full max-w-full rounded object-contain" />
        </div>
      )}
    </div>
  );
};

const ProjectDetail = ({ projectId, onBack, onUpdated }) => {
  const [, setSearchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [reply, setReply] = useState(null); // {reply_pending, last_invoice_subject}
  useEffect(() => {
    setReply(null);
    api.get(`/projects/${projectId}/reply-pending`)
      .then((r) => setReply(r.data))
      .catch(() => {});
  }, [projectId]);
  const confirm = useConfirm();
  const [customerOptions, setCustomerOptions] = useState([]);
  const [manualCustomer, setManualCustomer] = useState(false);
  const [customerDraft, setCustomerDraft] = useState("");

  useEffect(() => {
    let cancel = false;
    api
      .get(`/projects/${projectId}`)
      .then((r) => !cancel && setProject(r.data))
      .catch(() => toast.error("Could not load project"));
    return () => {
      cancel = true;
    };
  }, [projectId]);

  // Full refetch — used after rollup mutations (bill toggles, bill-all) whose
  // PUT/PATCH responses don't include the live rollup fields.
  const refresh = () =>
    api
      .get(`/projects/${projectId}`)
      .then((r) => setProject(r.data))
      .catch(() => toast.error("Could not load project"));

  // "7% bullshit" checkbox under the progress claims table.
  const toggleSevenPct = async (e) => {
    const on = e.target.checked;
    try {
      await api.put(`/projects/${projectId}`, { seven_pct_addon: on });
      setProject((p) => ({ ...p, seven_pct_addon: on }));
      toast.success(on ? "7% discount added to invoices" : "7% discount removed");
    } catch {
      toast.error("Could not save that option");
    }
  };

  const saveMultiplier = async (n) => {
    try {
      await api.put(`/projects/${projectId}`, { profit_multiplier: n });
      toast.success(`Profit multiplier set to ×${n}`);
      refresh();
      onUpdated?.();
    } catch {
      toast.error("Could not save multiplier");
    }
  };

  // Actual % of contract — null clears the override (back to auto/recommended).
  const saveActualPct = async (n) => {
    try {
      await api.put(`/projects/${projectId}`, { actual_billing_pct: n });
      toast.success(n == null ? "Actual % reset to recommended" : `Actual billing set to ${n}% of contract`);
      refresh();
      onUpdated?.();
    } catch {
      toast.error("Could not save actual %");
    }
  };

  // Per-line % to Date — manual completion tracking. Value to Date derives
  // from it (% × that line's contract value) and Bill % follows automatically.
  const saveLinePctToDate = async (idx, pctN) => {
    const next = (project.contract_lines || []).map((l, i) => {
      if (i !== idx) return l;
      if (pctN == null) { const { pct_to_date, ...rest } = l; return rest; }
      const p = Math.max(0, Math.min(100, Number(pctN) || 0));
      const c = Number(l.contract_value) || 0;
      return { ...l, pct_to_date: p, value_to_date: Math.round(c * (p / 100) * 100) / 100 };
    });
    try {
      await api.put(`/projects/${projectId}`, { contract_lines: next });
      refresh();
      onUpdated?.();
    } catch {
      toast.error("Could not save % to date");
    }
  };

  /** Generic contract-line field save (code / description / estimate # /
   * contract value) — every value on the sheet is editable. When the
   * contract value changes, value_to_date tracks the line's % to date. */
  const saveLineField = async (idx, field, val) => {
    const next = (project.contract_lines || []).map((l, i) => {
      if (i !== idx) return l;
      const out = { ...l, [field]: val };
      if (field === "contract_value") {
        const c = Number(val) || 0;
        const p = l.pct_to_date != null ? Number(l.pct_to_date) : (c > 0 && l.value_to_date != null ? (Number(l.value_to_date) / c) * 100 : 0);
        out.pct_to_date = Math.round(p * 100) / 100;
        out.value_to_date = Math.round(c * (p / 100) * 100) / 100;
      }
      return out;
    });
    try {
      await api.put(`/projects/${projectId}`, { contract_lines: next });
      refresh();
      onUpdated?.();
    } catch {
      toast.error("Could not save the contract line");
    }
  };

  /** Costs grid (Material / Wages / Overhead) — whole-sheet editable. */
  const saveCostField = async (idx, field, val) => {
    const next = (project.costs || []).map((c, i) => (i === idx ? { ...c, [field]: val } : c));
    try {
      await api.put(`/projects/${projectId}`, { costs: next });
      refresh();
      onUpdated?.();
    } catch {
      toast.error("Could not save the cost row");
    }
  };

  const addCostRows = async () => {
    const next = [
      { label: "Material", total_to_date: "", total: null, this_period: null, this_period_pct_increase: null, percent_to_bill: null },
      { label: "Wages", total_to_date: "", total: null, this_period: null, this_period_pct_increase: null, percent_to_bill: null },
      { label: "Overhead", total_to_date: "wage + material × 38%", total: null, this_period: null, this_period_pct_increase: null, percent_to_bill: null },
    ];
    try {
      await api.put(`/projects/${projectId}`, { costs: next });
      refresh();
      onUpdated?.();
    } catch {
      toast.error("Could not add the cost rows");
    }
  };

  /** One-tap: fill the costs sheet from the app's live labour/material
   * totals using the company's own formula — wages carry a 12% markup for
   * WSIB + vacation pay, overhead = (wages + material) × 38%. */
  const fillCostsFromApp = async () => {
    const sum = (rows) => (rows || []).reduce((s, r) => s + (Number(r.total) || 0), 0);
    const mCur = sum(project.current_materials);
    const lCur = sum(project.current_labor);
    const mAll = mCur + sum(project.billed_materials);
    const lAll = lCur + sum(project.billed_labor);
    const wCur = lCur * 1.12;
    const wAll = lAll * 1.12;
    const r2 = (n) => Math.round(n * 100) / 100;
    const m2 = (n) => r2(n).toFixed(2);
    const next = (project.costs || []).map((c) => {
      const label = (c.label || "").toLowerCase();
      if (label.startsWith("material")) return { ...c, total_to_date: m2(mAll), this_period: r2(mCur) };
      if (label.startsWith("wage")) return { ...c, total_to_date: m2(wAll), this_period: r2(wCur) };
      if (label.startsWith("overhead")) return { ...c, total_to_date: m2((mAll + wAll) * 0.38), this_period: r2((mCur + wCur) * 0.38) };
      return c;
    });
    try {
      await api.put(`/projects/${projectId}`, { costs: next });
      refresh();
      onUpdated?.();
      toast.success("Costs filled from current app data (wages ×1.12, overhead ×38%)");
    } catch {
      toast.error("Could not fill costs");
    }
  };

  /** Progress claims grid — every cell editable, claims addable. */
  const saveClaimField = async (claimNo, field, val) => {
    const next = (project.progress_claims || []).map((c) => (c.claim_no === claimNo ? { ...c, [field]: val } : c));
    try {
      await api.put(`/projects/${projectId}`, { progress_claims: next });
      refresh();
      onUpdated?.();
    } catch {
      toast.error("Could not save the progress claim");
    }
  };

  const addClaim = async () => {
    const claims = project.progress_claims || [];
    const nextNo = claims.reduce((m, c) => Math.max(m, Number(c.claim_no) || 0), 0) + 1;
    try {
      await api.put(`/projects/${projectId}`, { progress_claims: [...claims, { claim_no: nextNo, amount: null, holdback: null, percent: null }] });
      refresh();
      onUpdated?.();
    } catch {
      toast.error("Could not add the claim");
    }
  };


  // Reset: restore every auto value in the draw table + recommended box —
  // % to Date back to each line's last billed % (Previous Draw), Bill % back
  // to the even auto split, profit multiplier back to 1.3, Actual % back to
  // the recommended value.
  const resetBillingEdits = async () => {
    const ok = await confirm({
      title: "Reset billing to auto values?",
      description: "% to Date returns to each line's last billed % (Previous Draw), Bill % returns to the even auto split, profit multiplier back to 1.3, and Actual % back to the recommended value.",
      confirmLabel: "Reset",
    });
    if (!ok) return;
    // Snapshot pre-reset values so a misclick can be undone (one level deep).
    const snapshot = {
      actual_billing_pct: project.actual_billing_pct ?? null,
      profit_multiplier: project.profit_multiplier ?? null,
      contract_lines: lines.map((l) => ({ ...l })),
    };
    const next = lines.map((l) => {
      const c = Number(l.contract_value) || 0;
      const { pct_to_date, value_to_date, ...rest } = l;
      const out = { ...rest, bill_delta: 0 };
      if (l.prev_bill_pct != null) {
        out.pct_to_date = Number(l.prev_bill_pct);
        out.value_to_date = Math.round(c * (Number(l.prev_bill_pct) / 100) * 100) / 100;
      }
      return out;
    });
    try {
      await api.put(`/projects/${projectId}`, {
        actual_billing_pct: null,
        profit_multiplier: null,
        contract_lines: next,
      });
      toast.success("Billing reset to auto values", {
        duration: 10000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.put(`/projects/${projectId}`, snapshot);
              toast.success("Reset undone — previous values restored");
              refresh();
              onUpdated?.();
            } catch {
              toast.error("Could not undo the reset");
            }
          },
        },
      });
      refresh();
      onUpdated?.();
    } catch {
      toast.error("Could not reset billing");
    }
  };

  // Per-line Bill % — stored as a delta from the even share of this period's
  // actual billing %. Editing one line rebalances the others evenly so the
  // column always sums to (Σ % to Date + actual % being billed).
  const saveLineBillPct = async (idx, pctN) => {
    const cur = lines.map((l) => ({ pct: linePct(l), delta: Number(l.bill_delta) || 0 }));
    if (lines.length < 2) {
      toast.message("One line item — Bill % is always % to Date plus this period's share");
      return;
    }
    const target = pctN == null
      ? cur[idx].pct + lineShare            // cleared → this line back to auto
      : Math.max(0, Number(pctN) || 0);
    const newDelta = target - (cur[idx].pct + lineShare);
    const adj = newDelta - cur[idx].delta;  // how much the column sum would change
    const per = adj / (lines.length - 1);   // spread the opposite across the others
    const next = lines.map((l, i) => ({
      ...l,
      bill_delta: Math.round((i === idx ? newDelta : cur[i].delta - per) * 10000) / 10000,
    }));
    try {
      await api.put(`/projects/${projectId}`, { contract_lines: next });
      refresh();
      onUpdated?.();
    } catch {
      toast.error("Could not save Bill %");
    }
  };

  // Add line: appends a blank editable row straight into the draw table —
  // no popup. Item, description and value are filled in place, and the
  // row's trash can removes it if it was added by accident.
  const addBlankLine = async () => {
    try {
      await api.put(`/projects/${projectId}`, {
        contract_lines: [...lines, { code: "", description: "", estimate_number: "", contract_value: 0 }],
      });
      refresh();
      onUpdated?.();
    } catch {
      toast.error("Could not add a line");
    }
  };

  // Delete a contract line (confirm dialog) — removes it from the draw table.
  const deleteLine = async (idx) => {
    const l = lines[idx];
    const ok = await confirm({
      title: "Delete this contract line?",
      description: `${l.code || l.item || "Line"} — ${l.description || "no description"} (${money(l.contract_value)}) will be removed from the draw table. This cannot be undone.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await api.put(`/projects/${projectId}`, { contract_lines: lines.filter((_, i) => i !== idx) });
      toast.success("Line deleted");
      refresh();
      onUpdated?.();
    } catch {
      toast.error("Could not delete line");
    }
  };

  // Extras billing toggle — billed extras leave "To be invoiced" and land in
  // "Invoices sent" + this project's draw history as their own entry.
  const toggleExtraBilled = async (extraId, billed) => {
    try {
      await api.post(`/projects/${projectId}/extras/${extraId}/billed?billed=${billed}`);
      toast.success(billed ? "Extra marked billed" : "Extra unbilled");
      refresh();
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update extra");
    }
  };

  // Customers for the inline header picker (loaded with the page).
  useEffect(() => {
    api.get("/customers").then((r) => setCustomerOptions(r.data || [])).catch(() => {});
  }, []);

  // Inline customer change on the detail header — pick from the dropdown or
  // type manually; saves straight away (cascades to billing rows server-side).
  const saveCustomer = async (name) => {
    const trimmed = (name || "").trim();
    try {
      await api.put(`/projects/${projectId}`, { customer: trimmed });
      toast.success("Customer updated");
      refresh();
      onUpdated?.();
    } catch {
      toast.error("Could not save customer");
    }
  };
  const commitManualCustomer = () => {
    if (!manualCustomer) return;
    setManualCustomer(false);
    const t = customerDraft.trim();
    if (!t) return;  // empty manual entry cancels — never wipes the customer
    if (t !== (project.customer || project.customer_name || "")) saveCustomer(t);
  };

  // Header fields are always editable — no edit mode, no dialog. Each input
  // saves straight to the project on blur/Enter.
  const saveHeaderField = async (field, value) => {
    if (field === "name" && !value.trim()) {
      toast.error("Project name is required");
      return;
    }
    try {
      await api.put(`/projects/${projectId}`, { [field]: value });
      toast.success("Saved");
      refresh();
      onUpdated?.();
    } catch {
      toast.error("Could not save changes");
    }
  };

  // Buffered edits + undo/redo history for `extras`.
  const [pendingExtras, setPendingExtras] = useState(null); // null = matches server
  const [history, setHistory] = useState([]);   // past snapshots (for undo)
  const [future, setFuture] = useState([]);     // undone snapshots (for redo)
  const [savingAll, setSavingAll] = useState(false);
  const autoSaveTimer = useRef(null);

  // Section collapse — every box starts collapsed except the project header
  // (which has no toggle and is always visible).
  const [openBoxes, setOpenBoxes] = useState({});
  const toggleBox = (k) => setOpenBoxes((s) => ({ ...s, [k]: !s[k] }));

  // Push current server extras into history when we mutate.
  const applyExtras = (next) => {
    setHistory((h) => [...h.slice(-49), (pendingExtras ?? project?.extras ?? [])]);
    setFuture([]);
    setPendingExtras(next);
  };
  const undoExtras = () => {
    setHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      setFuture((f) => [(pendingExtras ?? project?.extras ?? []), ...f]);
      setPendingExtras(prev);
      return h.slice(0, -1);
    });
  };
  const redoExtras = () => {
    setFuture((f) => {
      if (f.length === 0) return f;
      const nxt = f[0];
      setHistory((h) => [...h, (pendingExtras ?? project?.extras ?? [])]);
      setPendingExtras(nxt);
      return f.slice(1);
    });
  };

  const saveAll = async (explicit = true) => {
    if (pendingExtras == null) {
      if (explicit) toast.message("No changes to save");
      return;
    }
    setSavingAll(true);
    try {
      const clean = pendingExtras.map((x) => ({
        id: x.id,
        include: !!x.include,
        item: (x.item || "").trim(),
        description: (x.description || "").trim(),
        cost_per: Number(x.cost_per) || 0,
        estimate_number: (x.estimate_number || "").trim(),
        unit: x.unit || "",
        qty: x.qty ? Number(x.qty) : 0,
        billed: !!x.billed,
        billed_at: x.billed_at || null,
      })).filter((x) => x.item || x.description || x.cost_per);
      await api.put(`/projects/${projectId}`, { extras: clean });
      refresh(); // PUT response lacks the rollup fields — refetch instead
      setPendingExtras(null);
      if (explicit) toast.success("Project saved");
      onUpdated?.();
    } catch (e) {
      if (explicit) toast.error(e?.response?.data?.detail || "Could not save project");
    } finally {
      setSavingAll(false);
    }
  };

  // Auto-save 800 ms after the last edit.
  useEffect(() => {
    if (pendingExtras == null) return undefined;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => saveAll(false), 800);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [pendingExtras]); // eslint-disable-line

  if (!project) {
    return <div className="bg-[#f4f5f8] p-8 text-center text-sm text-gray-500">Loading project…</div>;
  }

  const lines = project.contract_lines || [];
  const lineCount = lines.length;
  const lineContractTotal = lines.reduce((a, l) => a + (Number(l.contract_value) || 0), 0);
  // % to Date = manually-entered completion % per line (legacy rows fall back
  // to deriving it from the imported Value to Date). Value to Date = that %
  // of the line's contract value.
  const linePct = (l) => {
    if (l.pct_to_date != null && l.pct_to_date !== "") return Number(l.pct_to_date) || 0;
    const c = Number(l.contract_value) || 0;
    return c > 0 ? ((Number(l.value_to_date) || 0) / c) * 100 : 0;
  };
  const lineValue = (l) => (Number(l.contract_value) || 0) * (linePct(l) / 100);
  const lineValueToDate = lines.reduce((a, l) => a + lineValue(l), 0);
  // Rollup: every billed hours row + billed expense adds to Value to Date so
  // the top stats and the right-side "Total Completed to Date" reflect what's
  // actually been billed against the job.
  const billedLaborTotal = (project.billed_labor || []).reduce((s, r) => s + (Number(r.total) || 0), 0);
  const billedMaterialsTotal = (project.billed_materials || []).reduce((s, r) => s + (Number(r.cost) || 0), 0);
  const billedRollup = billedLaborTotal + billedMaterialsTotal;
  // Current-period (unbilled) rollup — feeds the recommended-billing calc.
  const currentLaborTotal = (project.current_labor || []).reduce((s, r) => s + (Number(r.total) || 0), 0);
  const currentMaterialsTotal = (project.current_materials || []).reduce((s, r) => s + (Number(r.cost) || 0), 0);
  const currentPeriodTotal = currentLaborTotal + currentMaterialsTotal;
  const totalContract = lineContractTotal || Number(project.value) || 0;
  // Recommended billing: current unbilled × profit multiplier (default 1.3,
  // tunable per project), percent = amount / total contract.
  const profitMultiplier = Number(project.profit_multiplier) > 0 ? Number(project.profit_multiplier) : 1.3;
  const recommendedBillingAmount = Math.max(0, currentPeriodTotal * profitMultiplier);
  const recommendedBillingPct = totalContract > 0 ? (recommendedBillingAmount / totalContract) : 0;
  // With linked QB POs (primary + extras): previous billings = the POs' sent
  // QuickBooks invoice SUBTOTALS (pre-tax, from the synced invoice cache);
  // Total Completed to Date = that sum + the current period's recommended
  // amount. Without a PO link: contract-lines previous draws / value-to-date
  // + billed overhead.
  const hasQboPo = Boolean(project.qbo_po_id || (project.extra_qbo_po_ids || []).length > 0);
  const prevBilling = hasQboPo
    ? (Number(project.qbo_invoiced_subtotal) || 0)
    : lines.reduce((a, l) => a + (Number(l.previous_draw) || 0), 0);
  const totalCompleted = hasQboPo
    ? prevBilling + recommendedBillingAmount
    : lineValueToDate + billedRollup;
  // Actual billing comes ONLY from the Actual % box (default: recommended).
  // Bill % and Balance to Finish are tracking columns — they never feed this.
  const actualIsAuto = project.actual_billing_pct == null;
  const actualBillingPct = actualIsAuto ? recommendedBillingPct * 100 : Number(project.actual_billing_pct);
  const actualBillingAmount = totalContract * (actualBillingPct / 100);
  const thisBilling = actualBillingAmount;
  // Bill % per line = % to Date + this period's even share + the line's manual
  // delta. Manual edits rebalance the other lines so the deltas always sum to
  // zero — i.e. Σ Bill % = Σ % to Date + actual % of contract being billed.
  const lineShare = lineCount > 0 ? actualBillingPct / lineCount : 0;
  const lineDelta = (l) => Number(l.bill_delta) || 0;
  const lineBillPct = (l) => linePct(l) + lineShare + lineDelta(l);
  // This Draw $ = the line's period share (share + delta) of the TOTAL contract.
  const lineDraw = (l) => ((lineShare + lineDelta(l)) / 100) * totalContract;
  // Balance to Finish = line contract value − value completed to date.
  const lineBalance = (l) => (Number(l.contract_value) || 0) - lineValue(l);
  const totalThisDraw = lines.reduce((a, l) => a + lineDraw(l), 0);
  const totalBalance = lines.reduce((a, l) => a + lineBalance(l), 0);
  const totalPrevDrawPct = lines.reduce((a, l) => a + (Number(l.prev_bill_pct) || 0), 0);
  // "7% extortion" option (checkbox under the progress claims table):
  // SUBTRACTS 7% of the billable amount BEFORE holdback and tax — shown on
  // the invoice as a "7% discount" line.
  const sevenPct = project.seven_pct_addon ? thisBilling * 0.07 : 0;
  const discounted = thisBilling - sevenPct;
  const holdback = discounted * 0.1;
  const netTotal = discounted - holdback;
  const hst = netTotal * 0.13;
  const totalPayable = netTotal + hst;

  const summaryRows = [
    ["Total Completed to Date", totalCompleted],
    ["Less: Previous Billing(s)", prevBilling],
    ["Total This Billing", thisBilling],
    ...(sevenPct > 0 ? [["7% discount", sevenPct]] : []),
    ["Less: Holdback", holdback],
    ["Net Total", netTotal],
    ["H.S.T.", hst],
    ["Total Payable", totalPayable],
  ];

  return (
    <div className="min-h-[80vh] bg-[#f4f5f8] p-4 text-gray-800 sm:p-6" data-testid="project-detail">
      <div className="mx-auto max-w-[1400px] space-y-4">
        {/* header bar */}
        <div className="flex items-center justify-between">
          <button
            onClick={onBack}
            data-testid="back-to-projects"
            className="flex items-center gap-1 text-sm font-semibold text-[#2c6eeb] hover:underline"
          >
            <ChevronLeft className="h-4 w-4" /> Projects
          </button>
          {/* extras auto-save on every change — undo/redo live up here now */}
          <div className="flex items-center gap-1">
            <CompleteProjectButton project={project} onChanged={() => { refresh(); onUpdated?.(); }} />
            {/* holdback stays visible even with every box collapsed */}
            <span className="mr-1 font-mono text-[10px] uppercase tracking-widest text-gray-500" data-testid="project-holdback-total" title="Holdback to date">
              Holdback <span className="font-semibold text-gray-800" data-testid="project-holdback-total-value">{money(project.qbo_holdback_total || 0)}</span>
            </span>
            {pendingExtras != null ? (
              <span className="mr-1 font-mono text-[10px] uppercase tracking-widest text-amber-600" data-testid="project-unsaved">
                {savingAll ? "Saving…" : "Auto-saving…"}
              </span>
            ) : (
              <span className="mr-1 font-mono text-[10px] uppercase tracking-widest text-emerald-600" data-testid="project-saved">Saved</span>
            )}
            <button
              onClick={undoExtras}
              disabled={history.length === 0}
              data-testid="project-undo-btn"
              className="rounded-md border border-gray-200 bg-white p-2 text-gray-600 hover:bg-gray-50 disabled:opacity-30"
              title="Undo"
              aria-label="Undo"
            >
              <Undo2 className="h-4 w-4" />
            </button>
            <button
              onClick={redoExtras}
              disabled={future.length === 0}
              data-testid="project-redo-btn"
              className="rounded-md border border-gray-200 bg-white p-2 text-gray-600 hover:bg-gray-50 disabled:opacity-30"
              title="Redo"
              aria-label="Redo"
            >
              <Redo2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* project header — all fields always editable, no edit mode */}
        <div className="relative rounded-lg border border-gray-200 bg-white p-5">
          <div className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm md:grid-cols-2">
            <HeaderEdit label="Project" value={project.name} strong dataTestid="project-name" onSave={(v) => saveHeaderField("name", v)} />
            <div className="flex items-center gap-2" data-testid="seven-pct-row" title="Subtracts 7% from the billable amount before holdback + tax — shown on the invoice as a 7% discount line">
              <span className="w-32 shrink-0 text-gray-400">7% Discount:</span>
              <input
                type="checkbox"
                checked={!!project.seven_pct_addon}
                onChange={toggleSevenPct}
                data-testid="seven-pct-checkbox"
                className="h-4 w-4 accent-[#1a3fd1]"
              />
              {project.seven_pct_addon && <span className="font-mono text-xs text-gray-500">−{money(thisBilling * 0.07)}</span>}
            </div>
            <HeaderEdit label="Progress Claim #" value={project.progress_claim_no} dataTestid="project-claim" onSave={(v) => saveHeaderField("progress_claim_no", v)} />
            <div className="flex items-center gap-2" data-testid="project-customer-field">
              <span className="w-32 shrink-0 text-gray-400">Customer:</span>
              {manualCustomer ? (
                <input
                  autoFocus
                  value={customerDraft}
                  onChange={(e) => setCustomerDraft(e.target.value)}
                  onBlur={commitManualCustomer}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitManualCustomer();
                    if (e.key === "Escape") setManualCustomer(false);
                  }}
                  data-testid="project-customer-manual-input"
                  placeholder="Type customer name…"
                  className="h-8 w-56 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-800 focus:border-[#1a3fd1] focus:outline-none"
                />
              ) : (
                <CustomerPicker
                  testid="project-customer-picker"
                  options={customerOptions}
                  label={project.customer || project.customer_name || "Set customer…"}
                  wide
                  onPick={(c) => saveCustomer(c.company_name || c.name)}
                  onManual={() => { setCustomerDraft(project.customer || project.customer_name || ""); setManualCustomer(true); }}
                />
              )}
            </div>
            <HeaderEdit label="Date" value={project.date} dataTestid="project-date" onSave={(v) => saveHeaderField("date", v)} />
            <QboPoDropdown project={project} onSaved={() => { refresh(); onUpdated?.(); }} />
            <div className="flex items-center gap-2" data-testid="project-status-field">
              <span className="w-32 shrink-0 text-gray-400">Status:</span>
              <select
                value={displayStatus(project.status)}
                onChange={(e) => saveHeaderField("status", e.target.value)}
                data-testid="project-status-select"
                className="h-7 rounded-md border border-transparent bg-transparent px-1 text-sm text-gray-700 hover:border-gray-200 focus:border-[#1a3fd1] focus:bg-white focus:outline-none"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <HeaderEdit label="Location" value={project.location} dataTestid="project-location" onSave={(v) => saveHeaderField("location", v)} />
            {reply?.reply_pending && (
              <div className="flex items-end pb-0.5" data-testid="project-reply-badge">
                <button
                  onClick={() => setSearchParams({ tab: "email", thread: reply.last_invoice_subject || `Invoice — ${project.name}` })}
                  title="Customer replied — open the conversation in Mail"
                  className="inline-flex items-center gap-1 rounded-full bg-[#1a3fd1] px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-[#ffffff] hover:bg-[#1633b8]"
                >
                  <Mail className="h-3 w-3" /> Reply
                </button>
              </div>
            )}
            <div className="md:col-span-2" data-testid="project-notes">
              <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400">Notes</p>
              <textarea
                key={project.notes || ""}
                defaultValue={project.notes || ""}
                placeholder="Add notes…"
                rows={project.notes ? 2 : 1}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v !== (project.notes || "")) saveHeaderField("notes", v);
                }}
                data-testid="project-notes-input"
                className="mt-0.5 w-full rounded-md border border-transparent bg-transparent px-2 py-1 text-sm text-gray-800 placeholder:text-gray-300 hover:border-gray-200 focus:border-[#1a3fd1] focus:bg-white focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Rip-out reports filed against this job — red box pinned to the
            top of the project page (reports + photos also stay in the
            Rip-outs log and the office email). */}
        <ProjectRipouts projectId={project.project_id} />

        {/* contract value + summary */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[2.4fr_1fr]">
          <div>
          {/* contract lines + extras — one collapsible box. Phone view keeps
              every field but merges the lines into a single box, Totals on top. */}
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white" data-testid="contract-lines-box">
            <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-3 py-2">
              <h3 className="font-mono text-[10px] font-semibold uppercase tracking-widest text-gray-500">Contract lines</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={addBlankLine}
                  data-testid="add-lines-btn"
                  className="inline-flex items-center gap-1 rounded-md bg-[#1a3fd1] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1633b8]"
                >
                  <Plus className="h-3.5 w-3.5" /> Add line
                </button>
                <CollapseBtn open={!!openBoxes.lines} onClick={() => toggleBox("lines")} testId="collapse-lines" />
              </div>
            </div>
            {!openBoxes.lines && lines.length > 0 && (
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 px-3 py-1.5" data-testid="strip-stats-lines">
                <StripStat label="Contract">{money(totalContract)}</StripStat>
                <StripStat label="This draw">{money(totalThisDraw)}</StripStat>
                <StripStat label="Balance to finish">{money(totalBalance)}</StripStat>
              </div>
            )}
            {!!openBoxes.lines && (<>
            {/* phone: single box — Totals first, then each line divided by a rule */}
            <div className="divide-y divide-gray-200 sm:hidden">
            {lines.length === 0 ? (
              <p className="p-6 text-center text-sm text-gray-400">No contract lines — tap Add line above</p>
            ) : (
              <>
                <div className="bg-gray-50 p-3">
                  <p className="mb-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-500">Totals</p>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <span className="text-gray-600">Contract</span><span className="text-right font-mono font-semibold text-gray-800">{money(totalContract)}</span>
                    <span className="text-gray-600">Value to Date</span><span className="text-right font-mono font-semibold text-gray-800">{money(lineValueToDate)}</span>
                    <span className="text-gray-600">% to Date</span><span className="text-right font-mono font-semibold text-gray-800">{totalContract > 0 ? `${((lineValueToDate / totalContract) * 100).toFixed(2)}%` : ""}</span>
                    <span className="text-gray-600">Previous Draw</span><span className="text-right font-mono font-semibold text-gray-800">{totalPrevDrawPct > 0 ? `${totalPrevDrawPct.toFixed(2)}%` : (prevBilling > 0 ? money(prevBilling) : "—")}</span>
                    <span className="text-gray-600">Bill %</span><span className="text-right font-mono font-semibold text-gray-800">{totalContract > 0 && lineCount > 0 ? `${(((lineValueToDate + totalThisDraw) / totalContract) * 100).toFixed(2)}%` : ""}</span>
                    <span className="text-gray-600">This Draw</span><span className="text-right font-mono font-semibold text-[#1a3fd1]">{money(totalThisDraw)}</span>
                    <span className="text-gray-600">Balance to Finish</span><span className="text-right font-mono font-semibold text-gray-800" data-testid="totals-balance-to-finish-mobile">{money(totalBalance)}</span>
                  </div>
                </div>
                {lines.map((l, i) => (
                <div key={i} className="p-3" data-testid={`contract-line-card-${i}`}>
                  <div className="flex items-baseline justify-between gap-2 border-b border-gray-100 pb-2">
                    <div className="flex items-baseline gap-2 min-w-0">
                      <span className="rounded bg-[#fff2cc] px-1.5 py-0.5 font-mono text-[11px] font-semibold text-gray-800">{l.code || l.item}</span>
                      <span className="truncate text-sm text-gray-700">{l.description}</span>
                    </div>
                    <span className="flex shrink-0 items-center gap-1">
                      <span className="font-mono text-sm font-semibold text-gray-800">{money(l.contract_value)}</span>
                      <button
                        onClick={() => deleteLine(i)}
                        data-testid={`line-delete-mobile-${i}`}
                        title="Delete line"
                        className="rounded p-1 text-gray-300 hover:text-red-500"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                    <span className="text-gray-500">% to Date</span>
                    <span className="text-right">
                      <LineBillPctInput
                        value={l.pct_to_date != null ? Number(l.pct_to_date) : (linePct(l) > 0 ? Math.round(linePct(l) * 100) / 100 : null)}
                        testId={`line-pct-date-mobile-${i}`}
                        onSave={(p) => saveLinePctToDate(i, p)}
                      />
                    </span>
                    <span className="text-gray-500">Value to Date</span><span className="text-right font-mono text-gray-800">{money(lineValue(l))}</span>
                    <span className="text-gray-500">Previous Draw</span><span className="text-right font-mono text-gray-800">{l.prev_bill_pct != null ? `${Number(l.prev_bill_pct).toFixed(2)}%` : (Number(l.previous_draw) > 0 ? money(l.previous_draw) : "—")}</span>
                    <span className="text-gray-500">Bill %</span><span className="text-right">{lineCount < 2 ? <span className="font-mono text-gray-800">{`${(Math.round(lineBillPct(l) * 100) / 100).toFixed(2)}%`}</span> : <LineBillPctInput value={Math.round(lineBillPct(l) * 100) / 100} testId={`line-bill-pct-mobile-${i}`} onSave={(p) => saveLineBillPct(i, p)} />}</span>
                    <span className="text-gray-500">This Draw</span><span className="text-right font-mono font-semibold text-[#1a3fd1]">{money(lineDraw(l))}</span>
                    <span className="text-gray-500">Balance</span><span className="text-right font-mono text-gray-800">{money(lineBalance(l))}</span>
                  </div>
                </div>
                ))}
              </>
            )}
            </div>

          {/* tablet / desktop view: full table */}
          <div className="hidden overflow-x-auto sm:block">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-[#e9e2d0] text-gray-700">
                  <Th>Item</Th><Th>Description</Th>
                  <Th className="text-center">Estimate #</Th>
                  <Th className="text-right">Contract Value</Th>
                  <Th className="text-right">% to Date</Th>
                  <Th className="text-right">Value to Date</Th>
                  <Th className="text-right">Previous Draw</Th>
                  <Th className="text-right">Bill %</Th>
                  <Th className="text-right bg-[#dbe7f6]">This Draw</Th>
                  <Th className="text-right">Balance to Finish</Th>
                  <Th className="w-8">{/* delete */}</Th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 && (
                  <tr><Td colSpan={11} className="py-6 text-center text-gray-400">No contract lines — use Add line above</Td></tr>
                )}
                {lines.map((l, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <Td className="bg-[#fff2cc] font-medium">
                      <CellEdit kind="text" value={l.code || l.item} testId={`line-code-edit-${i}`} onSave={(v) => saveLineField(i, "code", v)} />
                    </Td>
                    <Td className="bg-[#fff2cc]">
                      <CellEdit kind="text" value={l.description} testId={`line-desc-edit-${i}`} onSave={(v) => saveLineField(i, "description", v)} />
                    </Td>
                    <Td className="text-center">
                      <span className="inline-flex items-center gap-0.5">
                        <CellEdit kind="text" value={l.estimate_number} testId={`line-estimate-edit-${i}`} align="text-center" className="w-20" onSave={(v) => saveLineField(i, "estimate_number", v)} />
                        {l.estimate_number && <QboEstimateOpenBtn doc={l.estimate_number} />}
                      </span>
                    </Td>
                    <Td className="text-right">
                      <CellEdit kind="money" value={l.contract_value} testId={`line-value-edit-${i}`} align="text-right" className="w-24" onSave={(v) => saveLineField(i, "contract_value", v)} />
                    </Td>
                    <Td className="text-right">
                      <LineBillPctInput
                        value={l.pct_to_date != null ? Number(l.pct_to_date) : (linePct(l) > 0 ? Math.round(linePct(l) * 100) / 100 : null)}
                        testId={`line-pct-date-${i}`}
                        onSave={(p) => saveLinePctToDate(i, p)}
                      />
                    </Td>
                    <Td className="text-right" dataTestid={`line-value-to-date-${i}`}>{money(lineValue(l))}</Td>
                    <Td className="text-right bg-[#f3d9d9]" dataTestid={`line-prev-draw-${i}`}>
                      {l.prev_bill_pct != null ? `${Number(l.prev_bill_pct).toFixed(2)}%` : (Number(l.previous_draw) > 0 ? money(l.previous_draw) : "—")}
                    </Td>
                    <Td className="text-right">
                      {lineCount < 2
                        ? <span className="text-gray-700">{`${(Math.round(lineBillPct(l) * 100) / 100).toFixed(2)}%`}</span>
                        : <LineBillPctInput value={Math.round(lineBillPct(l) * 100) / 100} testId={`line-bill-pct-${i}`} onSave={(p) => saveLineBillPct(i, p)} />}
                    </Td>
                    <Td className="text-right bg-[#dbe7f6]" dataTestid={`line-this-draw-${i}`}>{money(lineDraw(l))}</Td>
                    <Td className="text-right" dataTestid={`line-balance-${i}`}>{money(lineBalance(l))}</Td>
                    <Td className="text-center">
                      <button
                        onClick={() => deleteLine(i)}
                        data-testid={`line-delete-${i}`}
                        title="Delete line"
                        className="rounded p-1 text-gray-300 hover:text-red-500"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </Td>
                  </tr>
                ))}
                <tr className="border-t-2 border-gray-300 font-semibold">
                  <Td colSpan={3} className="text-right">Totals</Td>
                  <Td className="text-right">{money(totalContract)}</Td>
                  <Td className="text-right">{totalContract > 0 ? `${((lineValueToDate / totalContract) * 100).toFixed(2)}%` : ""}</Td>
                  <Td className="text-right">{money(lineValueToDate)}</Td>
                  <Td className="text-right">{totalPrevDrawPct > 0 ? `${totalPrevDrawPct.toFixed(2)}%` : (prevBilling > 0 ? money(prevBilling) : "—")}</Td>
                  {/* Column total is contract-weighted (Σ value-to-date + Σ this draw) / contract —
                      i.e. % to Date total + actual %, never a raw sum of the % cells above. */}
                  <Td className="text-right" dataTestid="totals-bill-pct">{totalContract > 0 && lineCount > 0 ? `${(((lineValueToDate + totalThisDraw) / totalContract) * 100).toFixed(2)}%` : ""}</Td>
                  <Td className="text-right" dataTestid="totals-this-draw">{money(totalThisDraw)}</Td>
                  <Td className="text-right" dataTestid="totals-balance-to-finish">{money(totalBalance)}</Td>
                  <Td />
                </tr>
              </tbody>
            </table>
          </div>
          {/* extras — a separate area at the bottom of the contract-lines box */}
          <ProposedAdditions project={project} onChange={applyExtras} onToggleBilled={toggleExtraBilled} />
          </>)}
          </div>
          </div>

          {/* Recommended billing (current period × profit multiplier) */}
          <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4" data-testid="recommended-billing">
            <div className="mb-2 flex items-center justify-between">
              <p className="font-mono text-[10px] uppercase tracking-widest text-amber-700">Recommended Billing</p>
              <span className="flex items-center gap-1.5">
                <button
                  onClick={resetBillingEdits}
                  data-testid="billing-reset-btn"
                  title="Reset % to Date, Bill %, profit multiplier and Actual % to their auto values"
                  className="inline-flex items-center gap-1 rounded border border-amber-300 bg-white/60 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-amber-800 hover:bg-white"
                >
                  <RotateCcw className="h-3 w-3" /> Reset
                </button>
                <CollapseBtn open={!!openBoxes.billing} onClick={() => toggleBox("billing")} testId="collapse-billing" />
              </span>
            </div>
            {!openBoxes.billing && (
              <div className="mb-2 flex flex-wrap gap-x-4 gap-y-0.5" data-testid="strip-stats-billing">
                <StripStat label="This billing">{money(thisBilling)}</StripStat>
                <StripStat label="Bill %">{totalContract > 0 ? `${(((lineValueToDate + totalThisDraw) / totalContract) * 100).toFixed(2)}%` : "—"}</StripStat>
              </div>
            )}
            {!!openBoxes.billing && (
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-gray-600"><span>Current period (unbilled)</span><span className="font-mono">{money(currentPeriodTotal)}</span></div>
              <div className="flex items-center justify-between text-gray-600">
                <span>× profit multiplier</span>
                <ProfitMultiplierInput multiplier={profitMultiplier} onSave={saveMultiplier} />
              </div>
              <div className="mt-2 flex items-baseline justify-between border-t border-amber-200 pt-2">
                <span className="text-xs text-gray-500">Recommended amount</span>
                <span className="font-mono text-lg font-bold text-amber-800" data-testid="recommended-amount">{money(recommendedBillingAmount)}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-gray-500">Recommended % of contract</span>
                <span className="font-mono text-lg font-bold text-amber-800" data-testid="recommended-pct">{pct(recommendedBillingPct)}</span>
              </div>
              <div className="mt-2 flex items-center justify-between border-t border-amber-200 pt-2">
                <span className="text-xs text-gray-500">
                  Actual % of contract{actualIsAuto && <span className="ml-1 text-[10px] text-amber-700/70">(auto)</span>}
                </span>
                <ActualPctInput value={actualBillingPct} onSave={saveActualPct} />
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-gray-500">Actual billing amount</span>
                <span className="font-mono text-lg font-bold text-amber-900" data-testid="actual-amount">{money(actualBillingAmount)}</span>
              </div>
              <p className="mt-2 text-[10px] text-gray-500">
                Based on {money(currentPeriodTotal)} of current-period costs against a total job value of {money(totalContract)}.
              </p>
            </div>
            )}
          </div>

          {/* payable summary */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-2 flex items-center justify-between">
              <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Summary</p>
              <CollapseBtn open={!!openBoxes.summary} onClick={() => toggleBox("summary")} testId="collapse-summary" />
            </div>
            {!openBoxes.summary && (
              <div className="flex flex-wrap gap-x-4 gap-y-0.5" data-testid="strip-stats-summary">
                <StripStat label="Net total">{money(netTotal)}</StripStat>
                <StripStat label="Total payable">{money(totalPayable)}</StripStat>
              </div>
            )}
            {!!openBoxes.summary && (
            <table className="w-full text-sm">
              <tbody>
                {summaryRows.map(([label, val], i) => (
                  <tr key={label} className={i === summaryRows.length - 1 ? "border-t-2 border-gray-300 font-bold" : ""}>
                    <td className="py-1.5 text-gray-600">{label}</td>
                    <td className="py-1.5 text-right font-mono text-gray-800">{money(val)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
        </div>

        {(project.qbo_po_id || (project.extra_qbo_po_ids || []).length > 0) && <QboPoInvoices project={project} />}

        {/* Draw History — one row per billing cycle (bill-all / invoice send) */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm" data-testid="draw-log-card">
          <div className="flex items-center justify-between">
            <h3 className="font-mono text-[10px] font-semibold uppercase tracking-widest text-gray-500">Draw History</h3>
            <CollapseBtn open={!!openBoxes.draws} onClick={() => toggleBox("draws")} testId="collapse-draws" />
          </div>
          {!openBoxes.draws && (project.draw_log || []).length > 0 && (
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 pt-1.5" data-testid="strip-stats-draws">
              <StripStat label="Draws">{(project.draw_log || []).length}</StripStat>
              <StripStat label="Last draw">{money((project.draw_log || [])[(project.draw_log || []).length - 1]?.amount)}</StripStat>
            </div>
          )}
          {!!openBoxes.draws && (<>
          {(project.draw_log || []).length === 0 ? (
            <p className="mt-2 text-xs text-gray-400" data-testid="draw-log-empty">
              No billing cycles recorded yet — the trail starts with the next bill-all or invoice send.
            </p>
          ) : (
            <div className="mt-2 overflow-hidden rounded-lg border border-gray-100">
              {[...(project.draw_log || [])].reverse().map((d, i) => (
                <div key={i} data-testid={`draw-log-row-${i}`}
                  className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0">
                  <span className="font-mono text-xs text-gray-500">{d.billed_at ? new Date(d.billed_at).toLocaleDateString("en-CA") : "—"}</span>
                  <span className="font-mono text-xs text-gray-500">{d.invoice_no ? `Inv #${d.invoice_no}` : ""}</span>
                  <span className="font-mono text-xs text-gray-600">{Number(d.pct || 0).toFixed(2)}%</span>
                  <span className="font-mono text-sm font-semibold text-[#1a3fd1]">{money(d.amount)}</span>
                </div>
              ))}
            </div>
          )}
          </>)}
        </div>

        {/* costs — Material / Wages / Overhead, every value editable */}
        <div className="rounded-lg border border-gray-200 bg-white" data-testid="costs-section">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-3">
            <div>
              <h3 className="text-sm font-bold text-gray-800">Costs</h3>
              <p className="text-xs text-gray-400">Material / Wages / Overhead — wages include 12% for WSIB + vacation pay; overhead = (wage + material) × 38%</p>
            </div>
            {(project.costs || []).length === 0 ? (
              <button
                onClick={addCostRows}
                data-testid="costs-add-rows-btn"
                className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                <Plus className="h-4 w-4" /> Add cost rows
              </button>
            ) : (
              <button
                onClick={fillCostsFromApp}
                data-testid="costs-fill-btn"
                title="Fill This Period + Total to Date from the app's labour/materials (wages ×1.12, overhead ×38%)"
                className="flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-800 hover:bg-amber-100"
              >
                <RefreshCw className="h-4 w-4" /> Fill from app data
              </button>
            )}
            <CollapseBtn open={!!openBoxes.costs} onClick={() => toggleBox("costs")} testId="collapse-costs" />
          </div>
        {!openBoxes.costs && (project.costs || []).length > 0 && (
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 px-4 py-2" data-testid="strip-stats-costs">
            <StripStat label="Rows">{(project.costs || []).length}</StripStat>
            <StripStat label="Total costs">{money((project.costs || []).reduce((s, c) => s + (Number(c.total) || 0), 0))}</StripStat>
          </div>
        )}

        {!!openBoxes.costs && (<>
        {/* costs — mobile stacked cards */}
        <div className="space-y-2 p-3 sm:hidden">
          {(project.costs || []).map((c, i) => (
            <div key={i} className="rounded-lg border border-gray-200 bg-gray-50 p-3" data-testid={`cost-card-${i}`}>
              <div className="mb-2 border-b border-gray-200 pb-1.5">
                <span className="text-sm font-semibold text-gray-800">{c.label}</span>
              </div>
              <div className="grid grid-cols-2 items-center gap-x-3 gap-y-1.5 text-xs">
                <span className="text-gray-500">Total to Date</span>
                <CellEdit kind="text" value={c.total_to_date} testId={`cost-todate-edit-${i}`} align="text-right" onSave={(v) => saveCostField(i, "total_to_date", v)} />
                <span className="text-gray-500">Total</span>
                <CellEdit kind="money" value={c.total} testId={`cost-total-edit-${i}`} align="text-right" onSave={(v) => saveCostField(i, "total", v)} />
                <span className="text-gray-500">This Period</span>
                <CellEdit kind="money" value={c.this_period} testId={`cost-period-edit-${i}`} align="text-right" onSave={(v) => saveCostField(i, "this_period", v)} />
                <span className="text-gray-500">% Increase</span>
                <CellEdit kind="pct" value={c.this_period_pct_increase} testId={`cost-pctinc-edit-${i}`} align="text-right" onSave={(v) => saveCostField(i, "this_period_pct_increase", v)} />
                <span className="text-gray-500">% to Bill</span>
                <CellEdit kind="pct" value={c.percent_to_bill} testId={`cost-tobill-edit-${i}`} align="text-right" className="bg-[#fff2cc]" onSave={(v) => saveCostField(i, "percent_to_bill", v)} />
              </div>
            </div>
          ))}
        </div>

        {/* costs — tablet / desktop table */}
        {(project.costs || []).length > 0 && (
        <div className="hidden overflow-x-auto sm:block">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="bg-[#e9e2d0] text-gray-700">
                <Th>Costs</Th>
                <Th className="text-right">Total to Date</Th>
                <Th className="text-right">Total</Th>
                <Th className="text-right">This Period</Th>
                <Th className="text-right">This Period % Increase</Th>
                <Th className="text-right">Percent to Bill This Period</Th>
              </tr>
            </thead>
            <tbody>
              {(project.costs || []).map((c, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <Td className="font-medium">{c.label}</Td>
                  <Td className="text-right"><CellEdit kind="text" value={c.total_to_date} testId={`cost-todate-edit-${i}`} align="text-right" className="w-24" onSave={(v) => saveCostField(i, "total_to_date", v)} /></Td>
                  <Td className="text-right"><CellEdit kind="money" value={c.total} testId={`cost-total-edit-${i}`} align="text-right" className="w-24" onSave={(v) => saveCostField(i, "total", v)} /></Td>
                  <Td className="text-right"><CellEdit kind="money" value={c.this_period} testId={`cost-period-edit-${i}`} align="text-right" className="w-24" onSave={(v) => saveCostField(i, "this_period", v)} /></Td>
                  <Td className="text-right"><CellEdit kind="pct" value={c.this_period_pct_increase} testId={`cost-pctinc-edit-${i}`} align="text-right" className="w-20" onSave={(v) => saveCostField(i, "this_period_pct_increase", v)} /></Td>
                  <Td className="text-right bg-[#fff2cc]"><CellEdit kind="pct" value={c.percent_to_bill} testId={`cost-tobill-edit-${i}`} align="text-right" className="w-20" onSave={(v) => saveCostField(i, "percent_to_bill", v)} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        </>)}
        </div>

        {/* overhead: employees + materials side-by-side */}
        <OverheadSection
          project={project}
          totalCompleted={totalCompleted}
          onSaved={(p) => { if (p) setProject(p); else refresh(); onUpdated?.(); }}
        />

        {/* progress claims — collapsible box; Add claim lives in the header */}
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white" data-testid="claims-section">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5">
            <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Progress claims — click any cell to edit</p>
            <div className="flex items-center gap-2">
              <button
                onClick={addClaim}
                data-testid="claim-add-btn"
                className="flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              >
                <Plus className="h-3.5 w-3.5" /> Add claim
              </button>
              <CollapseBtn open={!!openBoxes.claims} onClick={() => toggleBox("claims")} testId="collapse-claims" />
            </div>
          </div>
          {!openBoxes.claims && (project.progress_claims || []).length > 0 && (
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 px-4 py-2" data-testid="strip-stats-claims">
              <StripStat label="Claims">{(project.progress_claims || []).length}</StripStat>
              <StripStat label="Total">{money((project.progress_claims || []).reduce((s, c) => s + (Number(c.amount) || 0), 0))}</StripStat>
            </div>
          )}
          {!!openBoxes.claims && (<>
          <div className="divide-y divide-gray-100 sm:hidden">
          {(project.progress_claims || []).map((c) => (
            <div key={c.claim_no} className="p-3" data-testid={`progress-claim-card-${c.claim_no}`}>
              <div className="mb-2 flex items-baseline justify-between border-b border-gray-100 pb-1.5">
                <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Claim #</span>
                <span className="text-sm font-semibold text-gray-800">{c.claim_no}</span>
              </div>
              <div className="grid grid-cols-2 items-center gap-x-3 gap-y-1.5 text-xs">
                <span className="text-gray-500">Amount</span>
                <CellEdit kind="money" value={c.amount} testId={`claim-amount-edit-${c.claim_no}`} align="text-right" onSave={(v) => saveClaimField(c.claim_no, "amount", v)} />
                <span className="text-gray-500">10% Holdback</span>
                <CellEdit kind="money" value={c.holdback} testId={`claim-holdback-edit-${c.claim_no}`} align="text-right" onSave={(v) => saveClaimField(c.claim_no, "holdback", v)} />
                <span className="text-gray-500">Percent</span>
                <CellEdit kind="pct" value={c.percent} testId={`claim-percent-edit-${c.claim_no}`} align="text-right" onSave={(v) => saveClaimField(c.claim_no, "percent", v)} />
              </div>
            </div>
          ))}
          <div className="p-3" data-testid="progress-claim-card-total">
            <div className="mb-2 flex items-baseline justify-between border-b border-gray-100 pb-1.5">
              <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Total</span>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <span className="text-gray-500">Progress Claim Amount</span><span className="text-right font-mono font-semibold text-gray-800">{money(actualBillingAmount)}</span>
              <span className="text-gray-500">10% Holdback</span><span className="text-right font-mono font-semibold text-gray-800">{money(actualBillingAmount * 0.10)}</span>
              <span className="text-gray-500">Progress Claim Percent</span><span className="text-right font-mono font-semibold text-gray-800">{pct(actualBillingPct / 100)}</span>
            </div>
          </div>
        </div>

        <div className="hidden sm:block">
          <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="bg-[#e9e2d0] text-gray-700">
                <Th>Progress Claim #</Th>
                {(project.progress_claims || []).map((c) => (
                  <Th key={c.claim_no} className="text-right">{c.claim_no}</Th>
                ))}
                <Th className="text-right">Total</Th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Progress Claim Amount", "amount", money],
                ["10% Holdback", "holdback", money],
                ["Progress Claim Percent", "percent", pct],
              ].map(([label, key]) => {
                const claims = project.progress_claims || [];
                // Total column is live billing math, not an Excel sum:
                // amount = actual billing amount, holdback = 10% of it,
                // percent = actual % of contract.
                const totalCell =
                  key === "amount" ? money(actualBillingAmount)
                  : key === "holdback" ? money(actualBillingAmount * 0.10)
                  : pct(actualBillingPct / 100);
                return (
                  <tr key={key} className="border-b border-gray-100">
                    <Td className="font-medium">{label}</Td>
                    {claims.map((c) => (
                      <Td key={c.claim_no} className="text-right">
                        <CellEdit
                          kind={key === "percent" ? "pct" : "money"}
                          value={c[key]}
                          testId={`claim-${key}-edit-${c.claim_no}`}
                          align="text-right"
                          className="w-20"
                          onSave={(v) => saveClaimField(c.claim_no, key, v)}
                        />
                      </Td>
                    ))}
                    <Td className="text-right font-semibold" dataTestid={`progress-claim-total-${key}`}>{totalCell}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
          </>)}
        </div>
      </div>

    </div>
  );
};

const Th = ({ children, className = "" }) => (
  <th className={`border border-gray-200 px-3 py-2 text-left align-bottom font-semibold ${className}`}>{children}</th>
);
const Td = ({ children, className = "", colSpan, dataTestid }) => (
  <td colSpan={colSpan} data-testid={dataTestid} className={`border border-gray-200 px-3 py-2 ${className}`}>{children}</td>
);
// Key figure shown on a collapsed box strip.
const StripStat = ({ label, children }) => (
  <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
    {label} <span className="font-semibold normal-case text-gray-800">{children}</span>
  </span>
);

// More/Less collapse toggle — one per section box on the project page.
const CollapseBtn = ({ open, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    aria-expanded={open}
    className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500 hover:bg-gray-50"
  >
    {open ? <Minus className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
    {open ? "Less" : "More"}
  </button>
);

// Always-editable header field: a quiet input that saves on blur/Enter when
// the value changed (same pattern as the PO box). `key` resets the draft
// whenever the server value changes after a save.
const HeaderEdit = ({ label, value, onSave, strong, dataTestid }) => {
  const commit = (e) => {
    const v = e.target.value.trim();
    if (v !== (value || "")) onSave(v);
  };
  return (
    <div className="flex items-center gap-2">
      <span className="w-32 shrink-0 text-gray-400">{label}:</span>
      <input
        key={value || ""}
        defaultValue={value || ""}
        placeholder="—"
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        data-testid={dataTestid}
        className={`h-7 min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-2 text-sm placeholder:text-gray-300 hover:border-gray-200 focus:border-[#1a3fd1] focus:bg-white focus:outline-none ${strong ? "font-semibold text-gray-800" : "text-gray-700"}`}
      />
    </div>
  );
};

// Layout section: propose extra work / additions. Wires to `project.extras`
// via a parent-provided onSave callback (fired when the Save button below
// this section is pressed OR when a value is edited — parent decides).
const emptyRow = () => ({ id: Math.random().toString(36).slice(2), include: false, item: "", description: "", cost_per: "", estimate_number: "", unit: "", qty: "", percent_to_date: "", billed: false, billed_at: null });

const mapServerExtras = (list) => (list || []).map((x) => ({
  id: x.id || Math.random().toString(36).slice(2),
  include: !!x.include,
  item: x.item || "",
  description: x.description || "",
  cost_per: x.cost_per != null ? String(x.cost_per) : "",
  estimate_number: x.estimate_number || "",
  unit: x.unit || "",
  qty: x.qty != null ? String(x.qty) : "",
  percent_to_date: x.percent_to_date != null ? String(x.percent_to_date) : "",
  billed: !!x.billed,
  billed_at: x.billed_at || null,
}));

const ProposedAdditions = ({ project, onChange, onToggleBilled }) => {
  const initial = useMemo(() => {
    const existing = mapServerExtras(project?.extras);
    return existing.length ? existing : [emptyRow()];
  }, [project?.project_id]); // eslint-disable-line
  const [rows, setRows] = useState(initial);

  // Re-sync when server extras change outside this editor (e.g. billed
  // toggles). Skipped when identical so in-flight typing is never clobbered.
  useEffect(() => {
    const server = mapServerExtras(project?.extras);
    if (server.length === 0) return;
    setRows((cur) => (JSON.stringify(cur) === JSON.stringify(server) ? cur : server));
  }, [project?.extras]); // eslint-disable-line

  const bubble = (next) => {
    setRows(next);
    // Strip UI-only fields before handing to parent.
    if (onChange) {
      onChange(next.map((r) => ({
        id: r.id,
        include: r.include,
        item: r.item,
        description: r.description,
        cost_per: r.cost_per,
        estimate_number: r.estimate_number,
        unit: r.unit,
        qty: r.qty,
        percent_to_date: r.percent_to_date === "" ? null : parseFloat(r.percent_to_date) || 0,
        billed: r.billed,
        billed_at: r.billed_at,
      })));
    }
  };
  const setField = (id, field, val) => bubble(rows.map((r) => (r.id === id ? { ...r, [field]: val } : r)));
  const addRow = () => bubble([...rows, emptyRow()]);
  const removeRow = (id) => bubble(rows.length > 1 ? rows.filter((r) => r.id !== id) : rows);

  return (
    <div className="border-t border-gray-200 bg-white" data-testid="proposed-additions">
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <div>
          <h3 className="text-sm font-bold text-gray-800">Proposed Additions / Extra Work</h3>
          <p className="text-xs text-gray-400">
            One row per estimate — each estimate bills as its own separate invoice from the Billing tab.
          </p>
        </div>
        <button
          onClick={addRow}
          data-testid="add-proposed-item-btn"
          className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          <Plus className="h-4 w-4" /> Add item
        </button>
      </div>

      {/* Mobile stacked cards */}
      <div className="space-y-3 border-b border-gray-100 p-3 sm:hidden">
        {rows.map((r) => (
          <div key={r.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3" data-testid="proposed-item-card">
            <div className="mb-2 flex items-center justify-between gap-2 border-b border-gray-200 pb-2">
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <Checkbox
                  checked={r.include}
                  onCheckedChange={(v) => setField(r.id, "include", Boolean(v))}
                  className="border-gray-400 data-[state=checked]:border-[#2ca01c] data-[state=checked]:bg-[#2ca01c]"
                />
                <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Include · {r.include ? "Yes" : "No"}</span>
              </label>
              <button
                onClick={() => removeRow(r.id)}
                aria-label="Remove item"
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-500"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-2">
              <div>
                <label className="mb-1 block font-mono text-[9.5px] uppercase tracking-widest text-gray-500">Estimate #</label>
                <input value={r.estimate_number} onChange={(e) => setField(r.id, "estimate_number", e.target.value)} placeholder="Estimate #" data-testid={`extra-estimate-mobile-${r.id}`} className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 font-mono text-sm text-gray-800 focus:border-[#2ca01c] focus:outline-none" />
              </div>
              <div>
                <label className="mb-1 block font-mono text-[9.5px] uppercase tracking-widest text-gray-500">Item name</label>
                <input value={r.item} onChange={(e) => setField(r.id, "item", e.target.value)} placeholder="Item name" className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-800 focus:border-[#2ca01c] focus:outline-none" />
              </div>
              <div>
                <label className="mb-1 block font-mono text-[9.5px] uppercase tracking-widest text-gray-500">Description</label>
                <input value={r.description} onChange={(e) => setField(r.id, "description", e.target.value)} placeholder="Description" className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-800 focus:border-[#2ca01c] focus:outline-none" />
              </div>
              <div>
                <label className="mb-1 block font-mono text-[9.5px] uppercase tracking-widest text-gray-500">Contract value</label>
                <input value={r.cost_per} onChange={(e) => setField(r.id, "cost_per", e.target.value.replace(/[^0-9.-]/g, ""))} placeholder="0.00" className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-right text-sm text-gray-800 focus:border-[#2ca01c] focus:outline-none" />
              </div>
              <div>
                <label className="mb-1 block font-mono text-[9.5px] uppercase tracking-widest text-gray-500">% to date</label>
                <input value={r.percent_to_date} onChange={(e) => setField(r.id, "percent_to_date", e.target.value.replace(/[^0-9.]/g, ""))} placeholder="0" data-testid={`extra-pct-mobile-${r.id}`} className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-right text-sm text-gray-800 focus:border-[#2ca01c] focus:outline-none" />
              </div>
              {r.include && (
                <button
                  onClick={() => onToggleBilled?.(r.id, !r.billed)}
                  data-testid={`extra-bill-mobile-${r.id}`}
                  className={r.billed
                    ? "w-full rounded border border-emerald-300 bg-emerald-50 px-2 py-1.5 text-xs font-semibold text-emerald-700"
                    : "w-full rounded bg-[#2ca01c] px-2 py-1.5 text-xs font-semibold text-white"}
                >
                  {r.billed ? "✓ Billed — tap to unbill" : "Mark billed"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Tablet / desktop table */}
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-[#e9e2d0] text-gray-700">
              <Th className="text-center">Include</Th>
              <Th className="text-center">Estimate #</Th>
              <Th>Name</Th>
              <Th>Description</Th>
              <Th className="text-right">Contract Value</Th>
              <Th className="text-center">% to Date</Th>
              <Th className="text-right">Value to Date</Th>
              <Th className="text-center">Billed</Th>
              <Th className="text-center"> </Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-gray-100" data-testid="proposed-item-row">
                <Td className="text-center">
                  <div className="flex flex-col items-center gap-0.5">
                    <Checkbox
                      checked={r.include}
                      onCheckedChange={(v) => setField(r.id, "include", Boolean(v))}
                      data-testid="proposed-include-checkbox"
                      className="border-gray-400 data-[state=checked]:border-[#2ca01c] data-[state=checked]:bg-[#2ca01c]"
                    />
                    <span className="text-[10px] font-semibold text-gray-400">{r.include ? "Yes" : "No"}</span>
                  </div>
                </Td>
                <Td className="p-1 text-center">
                  <div className="flex items-center gap-1">
                    <input
                      value={r.estimate_number}
                      onChange={(e) => setField(r.id, "estimate_number", e.target.value)}
                      placeholder="—"
                      data-testid={`extra-estimate-${r.id}`}
                      className="w-20 rounded border border-gray-200 bg-white px-2 py-1.5 text-center font-mono text-gray-800 focus:border-[#2ca01c] focus:outline-none"
                    />
                    {r.estimate_number && <QboEstimateOpenBtn doc={r.estimate_number} />}
                  </div>
                </Td>
                <Td className="p-1">
                  <input
                    value={r.item}
                    onChange={(e) => setField(r.id, "item", e.target.value)}
                    placeholder="Name"
                    className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-gray-800 focus:border-[#2ca01c] focus:outline-none"
                  />
                </Td>
                <Td className="p-1">
                  <input
                    value={r.description}
                    onChange={(e) => setField(r.id, "description", e.target.value)}
                    placeholder="Description"
                    className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-gray-800 focus:border-[#2ca01c] focus:outline-none"
                  />
                </Td>
                <Td className="p-1">
                  <input
                    value={r.cost_per}
                    onChange={(e) => setField(r.id, "cost_per", e.target.value.replace(/[^0-9.-]/g, ""))}
                    placeholder="0.00"
                    data-testid={`extra-value-${r.id}`}
                    className="w-24 rounded border border-gray-200 bg-white px-2 py-1.5 text-right text-gray-800 focus:border-[#2ca01c] focus:outline-none"
                  />
                </Td>
                <Td className="p-1 text-center">
                  <span className="inline-flex items-center gap-0.5">
                    <input
                      value={r.percent_to_date}
                      onChange={(e) => setField(r.id, "percent_to_date", e.target.value.replace(/[^0-9.]/g, ""))}
                      placeholder="0"
                      data-testid={`extra-pct-${r.id}`}
                      className="w-14 rounded border border-gray-200 bg-white px-1 py-1.5 text-right font-mono text-gray-800 focus:border-[#2ca01c] focus:outline-none"
                    />
                    <span className="text-gray-400">%</span>
                  </span>
                </Td>
                <Td className="p-1 text-right font-mono text-gray-700" data-testid={`extra-value-to-date-${r.id}`}>
                  {money((parseFloat(r.cost_per) || 0) * ((parseFloat(r.percent_to_date) || 0) / 100))}
                </Td>
                <Td className="p-1 text-center">
                  {r.include ? (
                    r.billed ? (
                      <button
                        onClick={() => onToggleBilled?.(r.id, false)}
                        data-testid={`extra-unbill-${r.id}`}
                        className="rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-100"
                      >
                        ✓ Billed
                      </button>
                    ) : (
                      <button
                        onClick={() => onToggleBilled?.(r.id, true)}
                        data-testid={`extra-bill-${r.id}`}
                        className="rounded bg-[#2ca01c] px-2 py-1 text-[10px] font-semibold text-white hover:bg-[#248015]"
                      >
                        Mark billed
                      </button>
                    )
                  ) : (
                    <span className="text-[10px] text-gray-400">—</span>
                  )}
                </Td>
                <Td className="text-center">
                  <button
                    onClick={() => removeRow(r.id)}
                    aria-label="Remove item"
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProjectDetail;

// -------------------- QBO Estimate # helpers --------------------
// Renders the estimate number as a clickable link that opens the estimate
// directly inside QuickBooks Online (matched by DocNumber).
const openQboEstimate = async (doc) => {
  if (!doc) return;
  try {
    const r = await api.get("/qbo/estimate-lookup", { params: { doc_number: doc } });
    if (r.data?.url) window.open(r.data.url, "_blank", "noopener");
    else toast.error("QBO returned no URL");
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Could not find estimate in QuickBooks");
  }
};
const EstimateNumberCell = ({ value }) => {
  if (!value) return <span className="text-gray-400">—</span>;
  return (
    <button
      type="button"
      onClick={() => openQboEstimate(value)}
      data-testid={`open-qbo-estimate-${value}`}
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[11px] text-[#0364b8] hover:bg-[#eef4fb] hover:underline"
      title="Open in QuickBooks"
    >
      {value} <ExternalLink className="h-3 w-3" />
    </button>
  );
};
const QboEstimateOpenBtn = ({ doc }) => (
  <button
    type="button"
    onClick={() => openQboEstimate(doc)}
    aria-label="Open in QuickBooks"
    title="Open in QuickBooks"
    data-testid={`open-qbo-estimate-btn-${doc}`}
    className="rounded p-1 text-[#0364b8] hover:bg-[#eef4fb]"
  >
    <ExternalLink className="h-3.5 w-3.5" />
  </button>
);

// ---------------- Overhead (Employees + Materials + Total) ----------------
const OverheadSection = ({ project, onSaved, totalCompleted }) => {
  const [, setSearchParams] = useSearchParams(); // billing rows deep-link to Expenses/Hours
  const [employees, setEmployees] = useState([]);  // full directory from /api/employees
  const [hoursMap, setHoursMap] = useState({});    // { employee_id: hoursString }
  const [assignedIds, setAssignedIds] = useState(() => new Set()); // employees shown as rows
  const [materials, setMaterials] = useState([]);  // [{description, cost}]
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const [billedOpen, setBilledOpen] = useState(false); // Billed-to-Date itemized rows collapsed by default
  const [currentOpen, setCurrentOpen] = useState(false); // Current-period rows collapsed by default

  const projectId = project.project_id;
  // Contract total for the draw-row % display (same derivation as the page).
  const drawContract = (project.contract_lines || []).reduce((a, l) => a + (Number(l.contract_value) || 0), 0) || Number(project.value) || 0;

  // Draw history: edit auto rows (display override), full CRUD on manual rows.
  const [drawEdit, setDrawEdit] = useState(null); // {kind:'auto'|'manual'|'new', billed_at?, draw_id?, date, pct, total, note}
  const [drawBusy, setDrawBusy] = useState(false);
  const saveDrawEdit = async (form) => {
    setDrawBusy(true);
    // Empty % means "auto" — send null, never the empty string.
    const norm = { date: form.date, pct: form.pct === "" || form.pct == null ? null : form.pct, total: form.total, note: form.note };
    try {
      if (drawEdit.kind === "new") {
        await api.post(`/projects/${projectId}/draws`, norm);
        toast.success("Draw added");
      } else if (drawEdit.kind === "manual") {
        await api.put(`/projects/${projectId}/draws/${drawEdit.draw_id}`, norm);
        toast.success("Draw updated");
      } else {
        await api.put(`/projects/${projectId}/draws/override`, {
          billed_at: drawEdit.billed_at, date: norm.date, pct: norm.pct, total: norm.total,
        });
        toast.success("Draw corrected");
      }
      setDrawEdit(null);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the draw");
    } finally { setDrawBusy(false); }
  };
  const deleteDraw = async (drawId) => {
    if (!window.confirm("Delete this manually-added draw?")) return;
    try {
      await api.delete(`/projects/${projectId}/draws/${drawId}`);
      toast.success("Draw deleted");
      onSaved?.();
    } catch { toast.error("Could not delete the draw"); }
  };
  const revertDraw = async (billedAt) => {
    try {
      await api.put(`/projects/${projectId}/draws/override`, { billed_at: billedAt, clear: true });
      toast.success("Back to the computed values");
      onSaved?.();
    } catch { toast.error("Could not revert the draw"); }
  };

  useEffect(() => {
    api.get("/employees").then((r) => setEmployees(r.data || [])).catch(() => setEmployees([]));
  }, []);

  useEffect(() => {
    const map = {};
    const ids = new Set();
    (project.employees || []).forEach((e) => {
      if (e.employee_id) {
        map[e.employee_id] = e.hours != null ? String(e.hours) : "";
        ids.add(e.employee_id);
      }
    });
    setHoursMap(map);
    setAssignedIds(ids);
    setMaterials(Array.isArray(project.materials) ? project.materials.map((m) => ({ ...m })) : []);
  }, [project]);

  const rateOf = (emp) => Number(emp.hourly_rate ?? emp.rate ?? 0) || 0;
  const parseNum = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  };

  // Only the assigned employees show up as rows (matches the "who worked
  // this job" list). We still keep the full directory around so the +Add
  // picker can offer everyone else.
  const assignedEmployees = useMemo(
    () => employees.filter((e) => assignedIds.has(e.employee_id)),
    [employees, assignedIds]
  );
  const unassignedEmployees = useMemo(
    () => employees
      .filter((e) => !assignedIds.has(e.employee_id))
      .filter((e) => {
        const q = pickerQuery.trim().toLowerCase();
        if (!q) return true;
        return (e.name || "").toLowerCase().includes(q);
      }),
    [employees, assignedIds, pickerQuery]
  );

  const totalWages = useMemo(
    () => assignedEmployees.reduce((s, emp) => s + parseNum(hoursMap[emp.employee_id]) * rateOf(emp), 0),
    [assignedEmployees, hoursMap]
  );
  const totalMaterial = useMemo(
    () => materials.reduce((s, m) => s + parseNum(m.cost), 0),
    [materials]
  );
  // Total billed to date mirrors the Progress Claim "Total Completed to
  // Date" calc (passed in from the parent) — NOT the sum of sent invoices.
  // Current period overhead: only the live unbilled rollup (labor + expenses),
  // NOT the overall job overhead.
  const currentLaborTotal = (project.current_labor || []).reduce((s, r) => s + (Number(r.total) || 0), 0);
  const currentMaterialsTotal = (project.current_materials || []).reduce((s, r) => s + (Number(r.cost) || 0), 0);
  const currentPeriodOverhead = currentLaborTotal + currentMaterialsTotal;

  const persistEmployees = (nextMap, nextIds = assignedIds) => {
    const payload = employees
      .filter((emp) => nextIds.has(emp.employee_id))
      .map((emp) => ({
        employee_id: emp.employee_id,
        name: emp.name,
        rate: rateOf(emp),
        hours: parseNum(nextMap[emp.employee_id]),
        total: parseNum(nextMap[emp.employee_id]) * rateOf(emp),
      }));
    api.put(`/projects/${project.project_id}`, { employees: payload })
      .then((r) => onSaved?.(r.data))
      .catch(() => toast.error("Could not save hours"));
  };

  const persistMaterials = (next) => {
    const clean = next.filter((m) => (m.description || "").trim() !== "" || parseNum(m.cost) !== 0);
    api.put(`/projects/${project.project_id}`, { materials: clean })
      .then((r) => onSaved?.(r.data))
      .catch(() => toast.error("Could not save materials"));
  };

  const onHoursChange = (empId, v) => {
    const clean = v.replace(/[^0-9.]/g, "");
    setHoursMap((prev) => ({ ...prev, [empId]: clean }));
  };
  const addEmployeeRow = (emp) => {
    setAssignedIds((prev) => {
      const next = new Set(prev);
      next.add(emp.employee_id);
      return next;
    });
    setHoursMap((prev) => ({ ...prev, [emp.employee_id]: prev[emp.employee_id] ?? "" }));
    setPickerOpen(false);
    setPickerQuery("");
  };
  const removeEmployeeRow = (empId) => {
    const nextMap = { ...hoursMap };
    delete nextMap[empId];
    const nextIds = new Set(assignedIds);
    nextIds.delete(empId);
    setHoursMap(nextMap);
    setAssignedIds(nextIds);
    persistEmployees(nextMap, nextIds);
  };
  const onMaterialField = (i, field, v) => {
    setMaterials((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: field === "cost" ? v.replace(/[^0-9.]/g, "") : v };
      return next;
    });
  };
  const addMaterial = () => setMaterials((p) => [...p, { description: "", cost: "" }]);
  const removeMaterial = (i) => {
    setMaterials((prev) => {
      const next = prev.filter((_, idx) => idx !== i);
      persistMaterials(next);
      return next;
    });
  };

  // Live rollup rows coming from the hours + expenses collections.
  const currentLabor = project.current_labor || [];
  const currentMats = project.current_materials || [];
  const billedLabor = project.billed_labor || [];
  const billedMats = project.billed_materials || [];
  // Open the source record for a billing row: expenses land in the Expenses
  // tab editor; labour rows in the Hours week log (entry_id packs
  // ts:<employee_id>:<week_start>:<day_idx>:<row_idx>).
  const openSourceRecord = (r) => {
    if (r.expense_id) {
      setSearchParams({ tab: "expenses", expense: r.expense_id });
      return;
    }
    const parts = String(r.entry_id || "").split(":");
    if (parts[0] === "ts" && parts.length >= 4 && parts[1] && /^\d{4}-\d{2}-\d{2}$/.test(parts[2])) {
      setSearchParams({ tab: "hours", week: parts[2], emp: parts[1], day: parts[3] });
    }
  };
  const toggleHoursBilled = async (entry_id, billed) => {
    try {
      await api.patch(`/hours/${entry_id}/billed?billed=${billed}`);
      onSaved?.();
      toast.success(billed ? "Marked billed" : "Marked unbilled");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };
  const toggleExpBilled = async (expense_id, billed) => {
    try {
      await api.patch(`/expenses/${expense_id}/billed?billed=${billed}`);
      onSaved?.();
      toast.success(billed ? "Marked billed" : "Marked unbilled");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };
  // One-click: mark every unbilled hours row + expense on this project billed.
  const billAll = async () => {
    try {
      const r = await api.post(`/projects/${project.project_id}/bill-all`);
      const n = (r.data?.hours_marked || 0) + (r.data?.expenses_marked || 0);
      toast.success(n ? `Marked ${n} item${n === 1 ? "" : "s"} billed` : "Nothing to bill");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not bill all"); }
  };

  return (
    <>
    {/* Current Period (live unbilled hours + expenses) */}
    <section className="mb-4 rounded-2xl border-2 border-[#4f6fe8]/40 bg-[#1a3fd1]/[0.05] p-3 sm:p-4" data-testid="current-period-live">
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#4f6fe8]">
          Current Period — Unbilled
          {project.qbo_last_invoice_date && (
            <span className="ml-2 normal-case tracking-normal text-gray-400" data-testid="current-period-watermark">since last invoice {project.qbo_last_invoice_date}</span>
          )}
        </p>
        <span className="flex items-center gap-2">
          <button
            onClick={() => setCurrentOpen((v) => !v)}
            data-testid="current-toggle"
            className="inline-flex items-center gap-1 rounded-md border border-[#4f6fe8]/40 bg-white px-2.5 py-1 text-[10px] font-semibold text-[#1a3fd1] hover:bg-[#eef3ff]"
          >
            {currentOpen ? <>Show less <ChevronUp className="h-3 w-3" /></> : <>Show more ({currentLabor.length + currentMats.length}) <ChevronDown className="h-3 w-3" /></>}
          </button>
          {currentOpen && (currentLabor.length > 0 || currentMats.length > 0) && (
            <button onClick={billAll} data-testid="bill-all-btn" className="rounded bg-[#2ca01c] px-3 py-1 text-[10px] font-semibold text-white hover:bg-[#248015]">Bill all</button>
          )}
        </span>
      </div>
      {currentOpen && (currentLabor.length === 0 && currentMats.length === 0 ? (
        <p className="text-xs text-gray-500">Nothing to bill yet. Hours + expenses assigned to this project show here.</p>
      ) : (
        <div className="space-y-2">
          {currentLabor.map((r) => (
            <div key={`h-${r.entry_id}`} onClick={() => openSourceRecord(r)} title="Open in the hours log"
              className="flex cursor-pointer items-center justify-between gap-3 rounded border border-gray-200 bg-white px-3 py-2 text-xs hover:border-[#1a3fd1]/50 hover:bg-[#eef3ff]/40" data-testid={`current-labor-${r.entry_id}`}>
              <span className="flex-1 truncate"><span className="text-gray-500">Labor</span> · <strong>{r.name}</strong> · {r.hours} hrs × ${r.rate}/hr · <span className="text-gray-400" data-testid="labor-row-date">{r.weekday ? `${r.weekday} ` : ""}{r.date}</span></span>
              <span className="font-mono">{money(r.total)}</span>
              <button onClick={(e) => { e.stopPropagation(); toggleHoursBilled(r.entry_id, true); }} data-testid={`bill-hours-${r.entry_id}`} className="rounded bg-[#2ca01c] px-2 py-1 text-[10px] font-semibold text-white hover:bg-[#248015]">Mark billed</button>
            </div>
          ))}
          {currentMats.map((r) => (
            <div key={`e-${r.expense_id}`} onClick={() => openSourceRecord(r)} title="Open in Expenses"
              className="flex cursor-pointer items-center justify-between gap-3 rounded border border-gray-200 bg-white px-3 py-2 text-xs hover:border-[#1a3fd1]/50 hover:bg-[#eef3ff]/40" data-testid={`current-material-${r.expense_id}`}>
              <span className="flex-1 truncate"><span className="text-gray-500">Expense</span> · <strong>{r.description}</strong> · <span className="text-gray-400">{r.date}</span></span>
              <span className="font-mono">{money(r.cost)}</span>
              <button onClick={(e) => { e.stopPropagation(); toggleExpBilled(r.expense_id, true); }} data-testid={`bill-exp-${r.expense_id}`} className="rounded bg-[#2ca01c] px-2 py-1 text-[10px] font-semibold text-white hover:bg-[#248015]">Mark billed</button>
            </div>
          ))}
        </div>
      ))}
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm">
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">Labour</span>
          <span className="ml-3 font-bold text-gray-800" data-testid="current-period-labor-total">{money(currentLaborTotal)}</span>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm">
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">Materials</span>
          <span className="ml-3 font-bold text-gray-800" data-testid="current-period-materials-total">{money(currentMaterialsTotal)}</span>
        </div>
        <div className="rounded-lg border border-[#4f6fe8]/40 bg-[#dce6ff] px-4 py-2 text-sm">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#4f6fe8]">Current period overhead</span>
          <span className="ml-3 font-bold text-[#1a3fd1]" data-testid="current-period-overhead-total">{money(currentPeriodOverhead)}</span>
        </div>
      </div>
    </section>

    {/* Billed to Date (carries the whole-job overhead total) — itemized rows
        collapse behind Show more/less; the total stays visible. */}
    <section className="rounded-2xl border-2 border-emerald-500/30 bg-emerald-50 p-3 sm:p-4" data-testid="billed-to-date">
      <div className="mb-3 flex items-center justify-between">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-emerald-700">Billed to Date</p>
        <button
          onClick={() => setBilledOpen((v) => !v)}
          data-testid="billed-toggle"
          className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-white px-2.5 py-1 text-[10px] font-semibold text-emerald-800 hover:bg-emerald-100"
        >
          {billedOpen ? <>Show less <ChevronUp className="h-3 w-3" /></> : <>Show more ({billedLabor.length + billedMats.length}) <ChevronDown className="h-3 w-3" /></>}
        </button>
      </div>
      {billedOpen && (billedLabor.length === 0 && billedMats.length === 0 ? (
        <p className="text-xs text-emerald-700/60">Nothing billed yet. Mark rows billed above and they land here.</p>
      ) : (
        <div className="space-y-2">
          {billedLabor.map((r) => (
            <div key={`bh-${r.entry_id}`} onClick={() => openSourceRecord(r)} title="Open in the hours log"
              className="flex cursor-pointer items-center justify-between gap-3 rounded border border-emerald-200 bg-white px-3 py-2 text-xs hover:border-emerald-400/60 hover:bg-emerald-50/50" data-testid={`billed-labor-${r.entry_id}`}>
              <span className="flex-1 truncate"><span className="text-emerald-600">✓ Labor</span> · <strong>{r.name}</strong> · {r.hours} hrs × ${r.rate}/hr{r.date ? <> · <span className="text-emerald-700/60">{r.weekday ? `${r.weekday} ` : ""}{r.date}</span></> : null}</span>
              <span className="font-mono">{money(r.total)}</span>
              <button onClick={(e) => { e.stopPropagation(); toggleHoursBilled(r.entry_id, false); }} data-testid={`unbill-hours-${r.entry_id}`} className="rounded border border-gray-300 bg-white px-2 py-1 text-[10px] font-semibold text-gray-700 hover:bg-gray-50">Unbill</button>
            </div>
          ))}
          {billedMats.map((r) => (
            <div key={`be-${r.expense_id}`} onClick={() => openSourceRecord(r)} title="Open in Expenses"
              className="flex cursor-pointer items-center justify-between gap-3 rounded border border-emerald-200 bg-white px-3 py-2 text-xs hover:border-emerald-400/60 hover:bg-emerald-50/50" data-testid={`billed-material-${r.expense_id}`}>
              <span className="flex-1 truncate"><span className="text-emerald-600">✓ Expense</span> · <strong>{r.description}</strong>{r.date ? <> · <span className="text-emerald-700/60">{r.date}</span></> : null}</span>
              <span className="font-mono">{money(r.cost)}</span>
              <button onClick={(e) => { e.stopPropagation(); toggleExpBilled(r.expense_id, false); }} data-testid={`unbill-exp-${r.expense_id}`} className="rounded border border-gray-300 bg-white px-2 py-1 text-[10px] font-semibold text-gray-700 hover:bg-gray-50">Unbill</button>
            </div>
          ))}
        </div>
      ))}
      <div className="mt-3 flex justify-end">
        <div className="theme-keep-bg rounded-lg border border-emerald-300 bg-emerald-100 px-4 py-2 text-sm">
          <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-700">Total billed to date</span>
          <span className="ml-3 font-bold text-emerald-900" data-testid="total-billed-to-date">{money(totalCompleted ?? 0)}</span>
        </div>
      </div>

      {/* Draw history — auto rows from billing events (editable via display
          override), manual rows (full add/edit/delete), plus sent invoices.
          Collapsed with the rest of the section until Show more. */}
      {billedOpen && (
      <div className="mt-3 border-t border-emerald-200 pt-3" data-testid="draw-history">
        <div className="mb-2 flex items-center justify-between">
          <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-700">Draw history</p>
          <button
            onClick={() => setDrawEdit({ kind: "new", date: "", pct: "", total: "", note: "" })}
            data-testid="draw-add-btn"
            className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-white px-2 py-1 text-[10px] font-semibold text-emerald-800 hover:bg-emerald-100"
          >
            <Plus className="h-3 w-3" /> Add item
          </button>
        </div>
        <div className="space-y-1">
          {[
            ...(project.draw_history || []).map((d) => ({ ts: d.date_override || d.billed_at, kind: "draw", d })),
            ...(project.manual_draws || []).map((m) => ({ ts: m.date, kind: "manual", m })),
            ...(project.sent_log || []).map((s) => ({ ts: s.sent_at, kind: "sent", s })),
          ].length === 0 && !drawEdit && (
            <p className="text-xs text-emerald-700/60">No draws yet — bill rows above or add one manually.</p>
          )}
          {[
            ...(project.draw_history || []).map((d) => ({ ts: d.date_override || d.billed_at, kind: "draw", d })),
            ...(project.manual_draws || []).map((m) => ({ ts: m.date, kind: "manual", m })),
            ...(project.sent_log || []).map((s) => ({ ts: s.sent_at, kind: "sent", s })),
          ]
            .sort((a, b) => String(b.ts).localeCompare(String(a.ts)))
            .map((e, i) => {
              const editing = drawEdit && ((drawEdit.kind === "auto" && e.kind === "draw" && drawEdit.billed_at === e.d.billed_at)
                || (drawEdit.kind === "manual" && e.kind === "manual" && drawEdit.draw_id === e.m.draw_id));
              if (editing) {
                return (
                  <DrawEditForm
                    key={`edit-${i}`}
                    value={drawEdit}
                    busy={drawBusy}
                    testid={`draw-edit-form-${i}`}
                    onChange={setDrawEdit}
                    onSave={() => saveDrawEdit({ date: drawEdit.date, pct: drawEdit.pct, total: drawEdit.total, note: drawEdit.note })}
                    onCancel={() => setDrawEdit(null)}
                  />
                );
              }
              if (e.kind === "draw") {
                const d = e.d;
                const pctText = d.pct_override != null
                  ? `${Number(d.pct_override).toFixed(2)}%`
                  : drawContract > 0 ? `${((d.total / drawContract) * 100).toFixed(2)}%` : `${d.item_count} item${d.item_count === 1 ? "" : "s"}`;
                return (
                  <div key={`d-${d.billed_at}-${i}`} className="flex items-center justify-between gap-3 rounded bg-white/70 px-3 py-1.5 text-xs" data-testid={`draw-${i}`}>
                    <span className="text-gray-600">
                      {fmtDateTime(d.date_override || d.billed_at)} · {pctText}
                      {d.edited && <span className="ml-1 rounded bg-amber-100 px-1 font-mono text-[9px] uppercase text-amber-700">edited</span>}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="font-mono font-semibold text-emerald-800">{money(d.total)}</span>
                      <button
                        onClick={() => setDrawEdit({ kind: "auto", billed_at: d.billed_at, date: (d.date_override || d.billed_at || "").slice(0, 10), pct: d.pct_override != null ? String(d.pct_override) : "", total: String(d.total ?? "") })}
                        title="Correct this draw's date / % / total" data-testid={`draw-edit-${i}`}
                        className="rounded border border-gray-300 bg-white p-1 text-gray-500 hover:bg-gray-50"
                      ><Pencil className="h-3 w-3" /></button>
                      {d.edited && (
                        <button onClick={() => revertDraw(d.billed_at)} title="Revert to computed values" data-testid={`draw-revert-${i}`}
                          className="rounded border border-gray-300 bg-white p-1 text-gray-500 hover:bg-gray-50"
                        ><RotateCcw className="h-3 w-3" /></button>
                      )}
                    </span>
                  </div>
                );
              }
              if (e.kind === "manual") {
                const m = e.m;
                const pctText = m.pct != null ? `${Number(m.pct).toFixed(2)}%` : drawContract > 0 ? `${((m.total / drawContract) * 100).toFixed(2)}%` : "";
                return (
                  <div key={`m-${m.draw_id}-${i}`} className="flex items-center justify-between gap-3 rounded bg-white/70 px-3 py-1.5 text-xs" data-testid={`draw-manual-${i}`}>
                    <span className="text-gray-600">
                      {m.date}{pctText ? ` · ${pctText}` : ""}
                      <span className="ml-1 rounded bg-emerald-100 px-1 font-mono text-[9px] uppercase text-emerald-700">manual</span>
                      {m.note ? ` · ${m.note}` : ""}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="font-mono font-semibold text-emerald-800">{money(m.total)}</span>
                      <button
                        onClick={() => setDrawEdit({ kind: "manual", draw_id: m.draw_id, date: m.date || "", pct: m.pct != null ? String(m.pct) : "", total: String(m.total ?? ""), note: m.note || "" })}
                        title="Edit this draw" data-testid={`draw-manual-edit-${i}`}
                        className="rounded border border-gray-300 bg-white p-1 text-gray-500 hover:bg-gray-50"
                      ><Pencil className="h-3 w-3" /></button>
                      <button onClick={() => deleteDraw(m.draw_id)} title="Delete this draw" data-testid={`draw-manual-delete-${i}`}
                        className="rounded border border-red-200 bg-white p-1 text-red-500 hover:bg-red-50"
                      ><Trash2 className="h-3 w-3" /></button>
                    </span>
                  </div>
                );
              }
              return (
                <div key={`s-${e.s.sent_at}-${i}`} className="flex items-center justify-between gap-3 rounded bg-[#eef3ff] px-3 py-1.5 text-xs" data-testid={`sent-${i}`}>
                  <span className="truncate text-gray-600">
                    {fmtDateTime(e.s.sent_at)} · {e.s.invoice_no ? `Invoice #${e.s.invoice_no} — emailed to ` : "invoice emailed to "}<span className="font-mono">{e.s.to}</span>
                    {e.s.kind === "extra" && e.s.label ? ` · ${e.s.label}` : ""}
                  </span>
                  <span className="shrink-0 font-mono font-semibold text-[#1a3fd1]">{money(e.s.total)}</span>
                </div>
              );
            })}
          {drawEdit?.kind === "new" && (
            <DrawEditForm
              value={drawEdit}
              busy={drawBusy}
              testid="draw-edit-form-new"
              onChange={setDrawEdit}
              onSave={() => saveDrawEdit({ date: drawEdit.date, pct: drawEdit.pct, total: drawEdit.total, note: drawEdit.note })}
              onCancel={() => setDrawEdit(null)}
            />
          )}
        </div>
      </div>
      )}
    </section>
    </>
  );
};

// ---------------- Draw history inline edit form (auto override / manual row) ----------------
const DrawEditForm = ({ value, onChange, onSave, onCancel, busy, testid }) => {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="rounded border border-emerald-300 bg-white px-3 py-2" data-testid={testid}>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <label className="text-[10px] uppercase tracking-widest text-gray-400">
          Date
          <input type="date" value={value.date} onChange={set("date")} data-testid={`${testid}-date`}
            className="mt-0.5 h-8 w-full rounded border border-gray-300 px-2 text-xs text-gray-800 focus:border-emerald-500 focus:outline-none" />
        </label>
        <label className="text-[10px] uppercase tracking-widest text-gray-400">
          % (optional)
          <input value={value.pct} onChange={(e) => onChange({ ...value, pct: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="auto"
            data-testid={`${testid}-pct`}
            className="mt-0.5 h-8 w-full rounded border border-gray-300 px-2 text-right font-mono text-xs text-gray-800 focus:border-emerald-500 focus:outline-none" />
        </label>
        <label className="text-[10px] uppercase tracking-widest text-gray-400">
          Amount $
          <input value={value.total} onChange={(e) => onChange({ ...value, total: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="0.00"
            data-testid={`${testid}-total`}
            className="mt-0.5 h-8 w-full rounded border border-gray-300 px-2 text-right font-mono text-xs text-gray-800 focus:border-emerald-500 focus:outline-none" />
        </label>
        <label className="text-[10px] uppercase tracking-widest text-gray-400">
          Note (optional)
          <input value={value.note || ""} onChange={set("note")} placeholder="e.g. paper invoice"
            data-testid={`${testid}-note`}
            className="mt-0.5 h-8 w-full rounded border border-gray-300 px-2 text-xs text-gray-800 focus:border-emerald-500 focus:outline-none" />
        </label>
      </div>
      <div className="mt-2 flex justify-end gap-2">
        <button onClick={onCancel} data-testid={`${testid}-cancel`}
          className="rounded border border-gray-300 bg-white px-3 py-1 text-[10px] font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
        <button onClick={onSave} disabled={busy} data-testid={`${testid}-save`}
          className="rounded bg-emerald-600 px-3 py-1 text-[10px] font-semibold text-white hover:bg-emerald-500 disabled:opacity-60">
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
};

// ---------------- Editable profit multiplier (Recommended Billing card) ----------------
const ProfitMultiplierInput = ({ multiplier, onSave }) => {
  const [v, setV] = useState(multiplier.toFixed(2));
  useEffect(() => setV(multiplier.toFixed(2)), [multiplier]);
  const commit = () => {
    const n = parseFloat(v);
    if (Number.isFinite(n) && n > 0 && n !== multiplier) onSave(n);
    else setV(multiplier.toFixed(2));
  };
  return (
    <span className="flex items-center gap-1 font-mono">
      ×
      <input
        value={v}
        onChange={(e) => setV(e.target.value.replace(/[^0-9.]/g, ""))}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        data-testid="profit-multiplier-input"
        className="w-16 rounded border border-amber-300 bg-white px-1 py-0.5 text-right font-mono text-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
      />
    </span>
  );
};

// ---------------- Actual % of contract input (Recommended Billing card) ----------------
const ActualPctInput = ({ value, onSave }) => {
  const [v, setV] = useState(value.toFixed(2));
  useEffect(() => setV(value.toFixed(2)), [value]);
  const commit = () => {
    const t = v.trim();
    if (t === "") { onSave(null); setV(value.toFixed(2)); return; }  // empty = back to auto
    const n = parseFloat(t);
    if (Number.isFinite(n) && n >= 0 && n !== value) onSave(n);
    // Always snap back to the computed value — covers the case where per-line
    // Bill % values derive the % and the prop doesn't change after saving.
    setV(value.toFixed(2));
  };
  return (
    <span className="flex items-center gap-1 font-mono">
      <input
        value={v}
        onChange={(e) => setV(e.target.value.replace(/[^0-9.]/g, ""))}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        data-testid="actual-pct-input"
        className="w-16 rounded border border-amber-300 bg-white px-1 py-0.5 text-right font-mono text-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
      />
      %
    </span>
  );
};

// ---------------- Per-line Bill % input (contract-lines table) ----------------
const LineBillPctInput = ({ value, onSave, testId }) => {
  const [v, setV] = useState(value != null ? String(value) : "");
  useEffect(() => setV(value != null ? String(value) : ""), [value]);
  const commit = () => {
    const t = v.trim();
    if (t === "") { if (value != null) onSave(null); return; }  // empty = clear
    const n = parseFloat(t);
    if (Number.isFinite(n) && n >= 0 && n !== value) onSave(n);
    else setV(value != null ? String(value) : "");
  };
  return (
    <span className="inline-flex items-center gap-0.5 font-mono text-xs">
      <input
        value={v}
        onChange={(e) => setV(e.target.value.replace(/[^0-9.]/g, ""))}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        placeholder="—"
        data-testid={testId}
        className="w-14 rounded border border-gray-300 bg-white px-1 py-0.5 text-right font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[#1a3fd1]"
      />
      %
    </span>
  );
};

/** Generic inline cell editor — looks like the sheet, types on click,
 * commits on blur/Enter, Esc reverts. kind: "text" | "money" | "pct"
 * (pct stores fractions, displays/edits whole percents like the Excel). */
const CellEdit = ({ value, onSave, testId, kind = "text", className = "", align = "text-left", placeholder = "—" }) => {
  const shown = (v) => {
    if (v === null || v === undefined || v === "") return "";
    if (kind === "pct") return String(Math.round(Number(v) * 10000) / 100);
    return String(v);
  };
  const [v, setV] = useState(shown(value));
  useEffect(() => setV(shown(value)), [value]);
  const commit = () => {
    const t = v.trim();
    if (t === shown(value)) return;
    if (t === "") { onSave(null); return; }
    if (kind === "money") {
      const n = parseFloat(t.replace(/[$,]/g, ""));
      if (!Number.isFinite(n)) { setV(shown(value)); return; }
      onSave(Math.round(n * 100) / 100);
      return;
    }
    if (kind === "pct") {
      const n = parseFloat(t.replace(/[%]/g, ""));
      if (!Number.isFinite(n)) { setV(shown(value)); return; }
      onSave(Math.round(n) / 10000 > 0 ? Math.round((n / 100) * 10000) / 10000 : 0);
      return;
    }
    onSave(t);
  };
  return (
    <input
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setV(shown(value)); e.currentTarget.blur(); } }}
      placeholder={placeholder}
      data-testid={testId}
      className={`w-full min-w-16 rounded border border-transparent bg-transparent px-1 py-0.5 font-mono text-xs text-gray-800 hover:border-gray-300 focus:border-[#1a3fd1] focus:bg-white focus:outline-none ${align} ${className}`}
      title="Click to edit — Enter/blur saves, Esc cancels"
    />
  );
};


// ---------------- QuickBooks PO dropdown (header field) + invoices sent to that PO ----------------
// Session-level cache for the QB PO list — the dropdown shows this instantly
// and revalidates in the background (server only pulls NEW/updated invoices).
let qboPoCache = { list: null, at: 0 };

const QboPoDropdown = ({ project, onSaved }) => {
  const [menu, setMenu] = useState(null); // null | "primary" | "extra"
  const [pos, setPos] = useState(qboPoCache.list); // null = not loaded yet
  const [query, setQuery] = useState("");
  const [listErr, setListErr] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const loadPos = async () => {
    setListErr("");
    try {
      const { data } = await api.get("/qbo/po-numbers");
      qboPoCache = { list: data || [], at: Date.now() };
      setPos(data || []);
    } catch (e) {
      setListErr(e?.response?.data?.detail || "Could not load QuickBooks POs");
      setPos([]);
    }
  };

  // Background revalidation: server upserts only invoices created/updated in
  // QuickBooks since the last sync, then hands back the fresh PO list.
  const refreshPos = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/qbo/po-numbers/refresh");
      qboPoCache = { list: data || [], at: Date.now() };
      setPos(data || []);
      setListErr("");
    } catch { /* keep the cached list — refresh is best effort */ }
    finally { setRefreshing(false); }
  };

  const openMenu = (mode) => {
    const next = menu === mode ? null : mode;
    setMenu(next);
    setQuery("");
    if (next) {
      if (pos === null) loadPos(); // instant: local cache
      refreshPos(); // background: pull only what's new in QuickBooks
    }
  };

  const extras = (project.extra_qbo_po_ids || []).filter(Boolean);
  const linked = new Set([project.qbo_po_id, ...extras].filter(Boolean).map((x) => x.toLowerCase()));

  const saveExtras = async (next) => {
    try {
      await api.put(`/projects/${project.project_id}`, { extra_qbo_po_ids: next });
      toast.success("PO list updated");
      onSaved?.();
    } catch { toast.error("Could not save the PO link"); }
  };

  const pick = async (po) => {
    setMenu(null);
    if (menu === "extra") {
      if (!po || linked.has(po.po.toLowerCase())) return;
      await saveExtras([...extras, po.po]);
      return;
    }
    try {
      await api.put(`/projects/${project.project_id}`, {
        qbo_po_id: po ? po.po : null,
        qbo_po_label: po ? po.po : null,
      });
      toast.success(po ? `PO ${po.po} linked` : "PO link cleared");
      onSaved?.();
    } catch { toast.error("Could not save the PO link"); }
  };

  const q = query.trim().toLowerCase();
  const shown = (pos || [])
    .filter((p) => !linked.has((p.po || "").toLowerCase()))
    .filter((p) => !q || (p.po || "").toLowerCase().includes(q))
    .slice(0, 50);

  // Manual PO — free-text entry saved on blur/Enter; lives ABOVE the
  // QuickBooks selection so a project can carry both.
  const saveManualPo = async (e) => {
    const v = e.target.value.trim();
    if (v === (project.po || "")) return;
    try {
      await api.put(`/projects/${project.project_id}`, { po: v });
      toast.success(v ? "PO saved" : "PO cleared");
      onSaved?.();
    } catch { toast.error("Could not save the PO"); }
  };

  const [syncing, setSyncing] = useState(false);

  // Sync % complete from the newest QuickBooks invoice sent to the linked
  // PO(s): the invoice's "X% Complete (previously billed Y%)" lines update
  // each line item's % to Date (+ Value to Date / Previous Draw) in order.
  // Runs AUTOMATICALLY when the project is opened — there is no button.
  const syncCompletion = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/projects/${project.project_id}/qbo/sync-completion`);
      if ((data?.applied || []).length > 0) {
        toast.success(`Synced from QuickBooks${data.invoice?.doc_number ? ` invoice #${data.invoice.doc_number}` : ""} — % to Date updated`);
      }
      onSaved?.(); // re-read the project so the draw table shows the new %
    } catch {
      // quiet on auto-sync — the page still works with the last known numbers
    } finally { setSyncing(false); }
  };

  // Fire once per project open / PO-set change. onSaved re-renders with the
  // same PO ids — the ref stops that echo from re-triggering the sync.
  const syncedForRef = useRef(null);
  useEffect(() => {
    const key = [...linked].sort().join("|");
    if (!key || syncedForRef.current === key) return;
    syncedForRef.current = key;
    syncCompletion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.qbo_po_id, project.extra_qbo_po_ids]);

  return (
    <div className="relative" data-testid="qbo-po-dropdown">
      <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400">PO</p>
      <input
        key={project.po || ""}
        defaultValue={project.po || ""}
        placeholder="Type a PO…"
        onBlur={saveManualPo}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        data-testid="po-manual-input"
        className="mt-0.5 h-7 w-full rounded-md border border-gray-200 bg-white px-2 text-sm font-medium text-gray-800 placeholder:text-gray-300 focus:border-[#1a3fd1] focus:outline-none"
      />
      <p className="mt-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-400">QB PO</p>
      <div className="mt-0.5 flex items-center gap-1">
        <button
          type="button"
          onClick={() => openMenu("primary")}
          data-testid="qbo-po-select"
          title="Pick from QuickBooks PO numbers"
          className="flex min-w-0 flex-1 items-center justify-between gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-left hover:border-[#1a3fd1]"
        >
          <span className={`truncate text-sm font-medium ${project.qbo_po_label ? "text-gray-800" : "text-gray-400"}`}>
            {project.qbo_po_label || "Select from QuickBooks…"}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400" />
        </button>
        <button
          type="button"
          onClick={() => openMenu("extra")}
          data-testid="qbo-po-add-btn"
          title="Add another QuickBooks PO to this project"
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:border-[#1a3fd1] hover:text-[#1a3fd1]"
        >
          <Plus className="h-4 w-4" />
        </button>
        {syncing && <RefreshCw className="h-3.5 w-3.5 shrink-0 animate-spin text-gray-300" data-testid="qbo-sync-auto" />}
      </div>
      {extras.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1" data-testid="qbo-po-extras">
          {extras.map((x) => (
            <span key={x} className="inline-flex items-center gap-1 rounded-full bg-[#eef3ff] px-2 py-0.5 font-mono text-[10px] font-semibold text-[#1a3fd1]" data-testid={`qbo-po-extra-${x}`}>
              {x}
              <button
                type="button"
                onClick={() => saveExtras(extras.filter((e) => e !== x))}
                data-testid={`qbo-po-extra-remove-${x}`}
                title="Remove this PO"
                className="text-[#1a3fd1]/60 hover:text-red-500"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {menu && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setMenu(null)} />
          <div className="absolute left-0 z-30 mt-1 w-80 max-w-[85vw] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl" data-testid="qbo-po-menu">
            <div className="flex items-center gap-1 border-b border-gray-100 p-1.5">
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search PO # or vendor…"
                data-testid="qbo-po-search"
                className="h-8 w-full rounded border border-gray-200 px-2 text-xs focus:border-[#1a3fd1] focus:outline-none"
              />
              {refreshing && <RefreshCw className="h-3.5 w-3.5 shrink-0 animate-spin text-gray-300" data-testid="qbo-po-refreshing" />}
            </div>
            <div className="max-h-56 overflow-y-auto" data-testid="qbo-po-list">
              {pos === null && <p className="px-3 py-2 text-xs text-gray-400">Loading…</p>}
              {listErr && <p className="px-3 py-2 text-xs text-red-500">{listErr}</p>}
              {menu === "primary" && project.qbo_po_id && (
                <button
                  type="button"
                  onClick={() => pick(null)}
                  data-testid="qbo-po-none"
                  className="block w-full border-b border-gray-100 px-3 py-2 text-left text-xs font-semibold text-red-500 hover:bg-red-50"
                >
                  — None (remove PO link) —
                </button>
              )}
              {pos !== null && !listErr && shown.length === 0 && <p className="px-3 py-2 text-xs text-gray-400">No PO numbers found on any QuickBooks invoice.</p>}
              {shown.map((p) => (
                <button
                  key={p.po}
                  type="button"
                  onClick={() => pick(p)}
                  data-testid={`qbo-po-option-${p.po}`}
                  className={`block w-full border-b border-gray-50 px-3 py-2 text-left text-xs last:border-b-0 hover:bg-[#eef3ff] ${project.qbo_po_id === p.po ? "bg-[#eef3ff]" : ""}`}
                >
                  <span className="font-mono font-semibold text-gray-800">{p.po}</span>
                  <span className="text-gray-500"> · {p.count} invoice{p.count === 1 ? "" : "s"}{p.last_date ? ` · last ${p.last_date}` : ""}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const QboPoInvoices = ({ project }) => {
  const [groups, setGroups] = useState(null); // null = not loaded yet; [{po, invoices}]
  const [invErr, setInvErr] = useState("");
  const [invOpen, setInvOpen] = useState(false); // collapsed by default
  const linkedPos = [project.qbo_po_id, ...(project.extra_qbo_po_ids || [])].filter(Boolean);
  useEffect(() => {
    if (!invOpen) return; // lazy: only pull when expanded (served from the synced cache)
    let cancelled = false;
    setGroups(null);
    setInvErr("");
    (async () => {
      try {
        const results = await Promise.all(
          linkedPos.map((po) => api.get(`/qbo/po-numbers/${encodeURIComponent(po)}/invoices`))
        );
        const gs = linkedPos.map((po, i) => ({
          po,
          invoices: (results[i].data || []).slice().sort((a, b) => (b.txn_date || "").localeCompare(a.txn_date || "")),
        }));
        if (!cancelled) setGroups(gs);
      } catch (e) {
        if (!cancelled) { setInvErr(e?.response?.data?.detail || "Could not load invoices for this PO"); setGroups([]); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.qbo_po_id, JSON.stringify(project.extra_qbo_po_ids || []), invOpen]);

  const totalCount = groups ? groups.reduce((s, g) => s + g.invoices.length, 0) : null;
  const multi = linkedPos.length > 1;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-3 sm:p-4" data-testid="qbo-po-invoices">
      <div className="flex items-center justify-between gap-2">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gray-500">
          QuickBooks invoices sent to {linkedPos.join(" + ")}{totalCount != null ? ` (${totalCount})` : ""}
        </p>
        <button
          onClick={() => setInvOpen((v) => !v)}
          data-testid="qbo-po-invoices-toggle"
          className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-[10px] font-semibold text-gray-700 hover:bg-gray-50"
        >
          {invOpen ? <>Show less <ChevronUp className="h-3 w-3" /></> : <>Show more <ChevronDown className="h-3 w-3" /></>}
        </button>
      </div>
      {invOpen && (
        <div className="mt-2">
          {groups === null && !invErr && <p className="text-xs text-gray-400">Loading…</p>}
          {invErr && <p className="text-xs text-red-500">{invErr}</p>}
          {groups !== null && totalCount === 0 && !invErr && (
            <p className="text-xs text-gray-400">No QuickBooks invoices reference this PO yet.</p>
          )}
          <div className="space-y-3">
            {(groups || []).map((g) => (
              <div key={g.po} data-testid={`qbo-po-group-${g.po}`}>
                {multi && (
                  <p className="mb-1 border-b border-gray-200 pb-1 font-mono text-[10px] font-bold uppercase tracking-widest text-[#1a3fd1]" data-testid={`qbo-po-group-header-${g.po}`}>
                    PO {g.po} · {g.invoices.length} invoice{g.invoices.length === 1 ? "" : "s"} · {money(g.invoices.reduce((s, i) => s + (Number(i.subtotal) || 0), 0))}
                  </p>
                )}
                <div className="space-y-1">
                  {g.invoices.map((inv) => (
                    <a
                      key={inv.id}
                      href={inv.qbo_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open this invoice in QuickBooks"
                      className="flex items-center justify-between gap-3 rounded bg-gray-50 px-3 py-1.5 text-xs hover:bg-[#eef3ff]"
                      data-testid={`qbo-po-invoice-${inv.id}`}
                    >
                      <span className="text-gray-600">{inv.txn_date} · Invoice #{inv.doc_number} · {inv.customer}</span>
                      <span className="flex items-center gap-1.5 font-mono font-semibold text-gray-800">
                        {money(inv.total)}
                        {inv.balance > 0
                          ? <span className="rounded bg-amber-100 px-1 text-[10px] text-amber-700">owes {money(inv.balance)}</span>
                          : <span className="rounded bg-emerald-100 px-1 text-[10px] text-emerald-700">paid</span>}
                        <ExternalLink className="h-3 w-3 text-gray-400" />
                      </span>
                    </a>
                  ))}
                  {g.invoices.length === 0 && (
                    <p className="text-xs text-gray-400">No QuickBooks invoices reference this PO yet.</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
